// src/views/OrchestrateurHorairesView.jsx — onglet « Demandes d'horaires »
// (espace ORCHESTRATEUR). Refonte du 2026-08-11, maquette validée Dimitri.
//
// ⚠️ CET ÉCRAN NE CONSTATE PAS, IL SERT À DÉCIDER. La question unique est
// « combien de temps reste-t-il pour le ménage si j'accepte ? », et ce nombre
// est le SUJET de l'écran : en gros, en haut de chaque carte, recalculé EN
// DIRECT pendant qu'on règle l'heure au curseur. La frise n'illustre pas la
// décision, elle est l'instrument qui la prend.
//
// ⚠️ GROUPÉ PAR JOURNÉE DE LOGEMENT, PAS PAR DEMANDE. Deux demandes le même
// jour sur le même logement se décident ENSEMBLE : accorder l'une change ce qui
// reste possible pour l'autre. La version précédente affichait une carte par
// demande, chacune calculant sa marge comme si l'autre n'existait pas.
//
// ⚠️ AUCUN MONTANT (décision Dimitri 2026-08-11). L'orchestrateur décide sur le
// TEMPS DE MÉNAGE, pas sur l'argent. Le mécanisme payant existe et reste
// entièrement SERVEUR : `services_orchestrateur_grant` fait la capture
// partielle, `services_orchestrateur_hourly_refuse` libère l'empreinte, et le
// montant n'est ni affiché, ni transmis, ni dérivable d'ici. Le corps envoyé ne
// porte QUE { action, booking_id, granted_time } — invariant conservé.
//
// PAS UN DEUXIÈME MÉCANISME : cet écran APPELLE l'existant
//   - liste  : services_orchestrateur_hourly_list
//   - accord : services_orchestrateur_grant
//   - refus  : services_orchestrateur_hourly_refuse
// Hub éteint (SERVICES_HUB_ENABLED ≠ "true") → listes vides ⇒ écran vide
// propre, jamais une erreur.
//
// ⚠️ ADMIN = LECTURE SEULE PAR DÉFAUT (décision Dimitri 2026-08-14). L'accord
// est un geste d'argent (capture partielle) : chez l'admin il passe par le mode
// « Modifier » local à cette vue — même triple signal que la frise du Suivi
// (bouton plein, libellé « Terminer », curseurs figés sinon). L'orchestrateur,
// lui, décide directement : c'est l'objet de son écran.

import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react'
import { DS } from '../lib/ui'
import CarteTitrePage, { boutonCarteTitre, dateDuJourFR } from '../components/CarteTitrePage'
import { supabase } from '../supabase'
import useEchapPourFermer from '../lib/useEchapPourFermer'
import { fetchLogInfo, invalidateLogInfo } from '../lib/menage-log-info-cache'
import CarteBordFrise, {
  FOND_PISTE_FRISE, HACHURE_DEMANDE_FRISE, HACHURE_CONTOUR,
  PLAGE_FOND, PLAGE_BORD, BLOC_MENAGE_FOND, REPERE_CONTRAT_TRAIT,
} from '../components/CarteBordFrise'
import { Clock, Bell, AlertTriangle, AlertCircle, CircleCheck, ArrowLeft, CalendarRange, LogOut, LogIn, RefreshCwOff } from 'lucide-react'
import {
  LATE, EARLY,
  seuilMenage, bornesContrat, simuler, verdict,
  bornesCurseur, aimanterDansBornes, heureInitiale,
  heuresPastilles,
  menageCommence, normaliserHeure, grouperParJournee, heurePourServeur,
  journeeEnConflit,
} from '../lib/horaires-decision'
import { formaterHeureCourte, formaterDuree, degradeBloc } from '../lib/menage-frise'
import { firstNameFromEmail } from './menage/RunCard'
import {
  computeVisibleEmails, buildRunsProviderFilter, todayParisIso, nowParisHHMM,
  voitToutLeParc,
} from './MenageTodayView'
import {
  versLaFrise, cheminRetour, libelleRetour, pourOnglet,
  ONGLET_HORAIRES, ONGLET_FRISE,
} from '../lib/orchestrateur-navigation'

// ── PALETTE — charte 2026. Le violet #7b6bb0 (LE violet de l'app — celui des
// interventions ; deux violets se confondaient, décision 2026-08-15) est
// part vers l'agent. Dès qu'un bandeau est violet, quelqu'un va être prévenu.
const C = {
  gris: '#3d5166', fonce: '#2e3d4d', ciel: '#87b8e6', cielPale: '#e4f0fb',
  colonne: '#f4f9fd', bord: '#d5e3ee', muet: '#98a2ad', texteDoux: '#5c6773',
  plage: '#eef4f9', plageBord: '#c3d5e4',
  accent: '#c1512d', bordAlerte: '#b8412b', texteAlerte: '#8f2f1d',
  fondAlerte: '#fdf4f0', blocConflit: '#f0c4b6',
  vertFond: '#f0f7f3', vertTexte: '#1f5a43', vertLabel: '#2c7a5c',
  notifFond: '#f2f0f8', notifIcone: '#7b6bb0', notifTexte: '#3e3463',
  bandeauAlerte: '#f5a892',
}

// Les curseurs natifs ne se stylent qu'en CSS (pseudo-éléments), et ils doivent
// marcher AU DOIGT : `touch-action: manipulation` empêche le navigateur de
// confisquer le geste pour faire défiler la page.
const CSS_HORAIRES = `
.fbz-h-bloc{transition:left .12s ease, width .12s ease}
@media (prefers-reduced-motion: reduce){ .fbz-h-bloc{transition:none} }
`

function formatDateFr(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(`${iso}T12:00:00`)
    if (Number.isNaN(d.getTime())) return iso
    // « lundi 17 août » — le jour de la semaine reste en minuscule, partout
    // (correction Dimitri 2026-08-16).
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
  } catch (_) { return iso }
}

function formatHeureDecision(validatedAt) {
  if (!validatedAt) return ''
  try {
    // « 13h30 », jamais « 13:30 » — LE format de la frise, partout (T5 2026-08-15).
    const d = new Date(validatedAt)
    const [h, m] = d.toLocaleTimeString('fr-FR', {
      timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit',
    }).split(':').map(Number)
    return formaterHeureCourte(h * 60 + m)
  } catch (_) { return '' }
}

/** « 14:00 » (Postgres TIME) → « 14h » — le format de la frise, partout. */
function heureCourteDeHHMM(hhmm) {
  const t = normaliserHeure(hhmm)
  if (!t) return '—'
  const [h, m] = t.split(':').map(Number)
  return formaterHeureCourte(h * 60 + m)
}

// POST authentifié vers le routeur beds24-mapping.
async function postAction(body) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Session expirée')
  const res = await fetch('/api/beds24-mapping', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(json?.error || `Erreur ${res.status}`)
    err.payload = json
    throw err
  }
  return json
}

// POST authentifié vers api/services — le chemin ADMIN de la confirmation
// (services_admin_validate porte les gardes H1/H3/H6 de la couche
// proposition/confirmation : un seul endroit, jamais dupliquées côté client).
async function postServices(body) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Session expirée')
  const res = await fetch(`/api/services?action=${encodeURIComponent(body.action)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(json?.error || `Erreur ${res.status}`)
    err.payload = json
    throw err
  }
  return json
}

// ── LA FRISE — pure PEINTURE de la journée du logement ──────────────────────
//
// Version finale (maquette Dimitri 2026-08-15/16) : le composant ne calcule
// plus rien — CarteDemande lui donne tout (occupation, hachures bleue/grises,
// cercles, bloc). Quatre couches, hauteur 36, langage du Suivi.
//   a) l'occupation #e2e5e9, qui SUIT l'heure annoncée valable — prénoms DANS
//      les zones, prénom seul ;
//   b) le bloc du ménage, qui PORTE le verdict : « Sofia · 3 h » (largeur et
//      étiquette partent de LA MÊME source : sim) ;
//   c) la hachure BLEUE = ce que LE VOYAGEUR RÉCLAME (jamais l'heure
//      sélectionnée) ; les hachures GRISES = ce que l'autre demande du jour
//      réclamerait ;
//   d) les repères : traits contrat, cercles d'annonce (verts — une annonce
//      valable ne peut QUE donner du temps ; gris barré si contredite).
// ⚠️ AUCUN élément de largeur nulle sur la piste (bug c de la maquette).

const HAUTEUR_PISTE_FRISE = 36
const RAYON_FRISE = 6


function FrisePeinture({
  axe, sim, seuilMin, agent, commence, nowMin, menageDebutMin = null,
  refDepart, refArrivee, arrivantConnu, prenomPartant, prenomArrivant,
  personnesPartant = null,
  personnesArrivant = null,
  hachures = [], cercles = [], etiquettesAxe = [], testId,
  hauteur = HAUTEUR_PISTE_FRISE,
}) {
  // La carte de bord doit savoir si son texte tient dans sa zone (règle :
  // jamais hors de la zone, icône seule sinon) — largeur de piste mesurée.
  const pisteRef = useRef(null)
  const [pisteLargeurPx, setPisteLargeurPx] = useState(0)
  useEffect(() => {
    const el = pisteRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setPisteLargeurPx(el.getBoundingClientRect().width))
    ro.observe(el)
    setPisteLargeurPx(el.getBoundingClientRect().width)
    return () => ro.disconnect()
  }, [])
  const insuffisant = sim.fin - sim.debut < seuilMin
  const avancement = commence && Number.isFinite(nowMin) && sim.fin > sim.debut
    ? Math.max(0, Math.min(100, ((nowMin - sim.debut) / (sim.fin - sim.debut)) * 100))
    : 0
  const pctL = (min) => ((min - axe.debut) / (axe.fin - axe.debut)) * 100
  const largeurL = (a, b) => Math.max(0, pctL(b) - pctL(a))
  // ⚠️ LA FRISE EST SEULE ET PLEINE LARGEUR (correction Dimitri 2026-08-16) :
  // ni colonne de libellé (la durée requise vit dans la phrase de verdict), ni
  // légende « départ / arrivée » (les heures vivent SUR L'AXE, dessous).
  // Grille d'heures : toutes les heures si la fenêtre est courte, sinon 1/2 —
  // en sautant celles qu'une étiquette (annonce, contrat) occupe déjà.
  const pas = axe.fin - axe.debut <= 8 * 60 ? 60 : 120
  const heuresAxe = []
  for (let h = Math.ceil(axe.debut / 60) * 60; h <= axe.fin; h += pas) {
    const prise = etiquettesAxe.some((e) => Math.abs(pctL(e.min) - pctL(h)) < 4)
    if (!prise) heuresAxe.push(h)
  }

  return (
    <div>
      <div style={{ flex: 1, minWidth: 0 }}>
          <div data-testid={testId} ref={pisteRef}
            style={{ position: 'relative', height: hauteur, background: FOND_PISTE_FRISE,
              borderRadius: RAYON_FRISE, overflow: 'hidden' }}>
            {/* Les CARTES DE BORD (2026-08-16) : mêmes cartes blanches que le
                Suivi — partant à gauche SANS heure (elle est sur l'axe),
                arrivant à droite avec la sienne. Jamais hors de leur zone. */}
            {/* LA PLAGE DE TRAVAIL — blanche bordée, du départ à l'arrivée
                (même dessin que le Suivi, direction 2026-08-16). */}
            <div aria-hidden="true"
              style={{ position: 'absolute', left: `${pctL(sim.debut)}%`,
                // ⚠️ LA PLAGE BLANCHE MONTRE LA FENÊTRE SIMULÉE (constat
                // Dimitri 2026-08-17 : « je clique et rien ne change ») —
                // choisir une pastille l'étire/la rétrécit EN DIRECT. Les
                // cartes de bord, elles, restent aux FAITS.
                width: `${largeurL(sim.debut, sim.fin)}%`,
                top: 0, height: '100%', background: PLAGE_FOND,
                border: `1px solid ${PLAGE_BORD}`, borderRadius: 5,
                boxSizing: 'border-box', zIndex: 1 }} />
            {pctL(refDepart) > 0.5 && (
              <CarteBordFrise cote="gauche" echelle="demandes"
                pctBord={pctL(refDepart)}
                prenom={prenomPartant} personnes={personnesPartant}
                heure={formaterHeureCourte(refDepart)}
                colleInterieur={hachures.some((z) => z.late)}
                largeurZonePx={(pctL(refDepart) / 100) * pisteLargeurPx}
                testId={`${testId}-carte-partant`} />
            )}
            {arrivantConnu && largeurL(refArrivee, axe.fin) > 0.5 && (
              <CarteBordFrise cote="droite" echelle="demandes"
                pctBord={pctL(refArrivee)}
                prenom={prenomArrivant} personnes={personnesArrivant}
                heure={formaterHeureCourte(refArrivee)}
                colleInterieur={hachures.some((z) => !z.late)}
                largeurZonePx={((100 - pctL(refArrivee)) / 100) * pisteLargeurPx}
                testId={`${testId}-carte-arrivant`} />
            )}


            {/* Les hachures se lisent SEULES — aucun libellé dessus. Toutes
                BLEUES : ce que chaque demande RÉCLAME, des deux côtés dans la
                même frise (carte unique, 2026-08-16). */}
            {/* Collées à la carte du voyageur : mêmes hauteurs qu'elle, pas
                d'arrondi ni de contour côté carte (version finale). */}
            {hachures.filter((z) => z.fin > z.debut).map((z) => (
              <React.Fragment key={z.cle}>
                <div data-testid={`horaires-reclame-${z.cle}`} aria-hidden="true"
                  style={{ position: 'absolute', left: `${pctL(z.debut)}%`,
                    width: `${largeurL(z.debut, z.fin)}%`, top: 4, bottom: 4,
                    borderRadius: z.late ? '0 4px 4px 0' : '4px 0 0 4px',
                    border: `1px dashed ${HACHURE_CONTOUR}`,
                    ...(z.late ? { borderLeft: 'none' } : { borderRight: 'none' }),
                    boxSizing: 'border-box',
                    background: HACHURE_DEMANDE_FRISE, zIndex: 3 }} />
              </React.Fragment>
            ))}

            {sim.fin > sim.debut && (() => {
              // ⚠️ OPTION A (arbitrage Dimitri 2026-08-17) : LE BLOC BLEU EST
              // LE MÉNAGE à sa durée planifiée, posé AU PLUS TÔT après le
              // départ — exactement le langage du Suivi. La fenêtre disponible
              // reste dite par la PLAGE BLANCHE ; le verdict continue d'écrire
              // les restants. Terracotta quand ça ne tient pas (même code).
              // ⚠️ LE MÉNAGE PLANIFIÉ FAIT FOI (Dimitri 2026-08-17) : si une
              // heure est fixée, le bloc se pose LÀ — comme au Suivi. Le
              // « au plus tôt » ne sert qu'aux ménages sans heure. Pendant la
              // simulation, le départ simulé peut pousser le bloc (max) : on
              // simule, mais le point de départ est l'heure réelle.
              const menageDebut = Math.max(
                Number.isFinite(menageDebutMin) ? menageDebutMin : sim.debut,
                sim.debut)
              // « Ça ne tient pas » à DEUX titres : la fenêtre est trop
              // courte (insuffisant), OU le ménage posé À SON HEURE déborde
              // de la fenêtre. Dans les deux cas : terracotta, le bloc occupe
              // ce qui reste et écrit sa durée réelle — le verdict en encart
              // dit ce qu'il faudrait. Sinon : bloc plein à la durée planifiée.
              const neTientPas = insuffisant || menageDebut + seuilMin > sim.fin
              const finBloc = neTientPas ? sim.fin : menageDebut + seuilMin
              // ⚠️ UN BLOC ÉTROIT NE COUPE PAS SON LIBELLÉ : sous 1 h le
              // libellé DÉBORDE sur fond blanc au lieu d'être rogné.
              const etroit = finBloc - menageDebut < 60
              return (
              <div data-testid={`${testId}-bloc`} className="fbz-h-bloc"
                style={{ position: 'absolute', left: `${pctL(menageDebut)}%`,
                  width: `${largeurL(menageDebut, finBloc)}%`, top: 0, height: '100%',
                  borderRadius: RAYON_FRISE, boxSizing: 'border-box',
                  background: neTientPas ? C.blocConflit : BLOC_MENAGE_FOND,
                  boxShadow: neTientPas ? `inset 0 0 0 2px ${C.bordAlerte}` : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  overflow: etroit ? 'visible' : 'hidden', zIndex: 4 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700,
                  color: neTientPas ? C.texteAlerte : '#fff',
                  fontFamily: DS.fontBody, whiteSpace: 'nowrap',
                  ...(etroit ? { background: 'rgba(255,255,255,.88)',
                    borderRadius: 4, padding: '1px 4px' } : {}) }}>
                  {agent ? `${firstNameFromEmail(agent) || agent} · ` : ''}{formaterDuree(Math.max(0, finBloc - menageDebut))}
                </span>
                {/* L'AVANCEMENT : bande 6 px en bas, remplie jusqu'au trait de
                    maintenant (direction 2026-08-16) — jamais sur l'alerte. */}
                {commence && !insuffisant && avancement > 0 && (
                  <div aria-hidden="true" style={{ position: 'absolute', left: 0,
                    right: 0, bottom: 0, height: 6, background: 'rgba(61,81,102,.16)' }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0,
                      width: `${Math.max(0, Math.min(100, avancement))}%`,
                      background: '#233240' }} />
                  </div>
                )}
              </div>
              )
            })()}

            {etiquettesAxe.filter((e) => e.trait).map((e) => (
              <div key={`t-${e.cle}`} data-testid={`horaires-repere-${e.cle}`}
                title="Heure du contrat"
                style={{ position: 'absolute', left: `${pctL(e.min)}%`, top: -2,
                  height: hauteur + 4, width: 2, background: REPERE_CONTRAT_TRAIT, zIndex: 5 }} />
            ))}

            {cercles.map((c2) => (
              <span key={c2.cle} data-testid={`horaires-annonce-cercle-${c2.cle}`}
                style={{ position: 'absolute', left: `${pctL(c2.min)}%`,
                  top: hauteur / 2 - 6, transform: 'translateX(-50%)',
                  width: 13, height: 13, borderRadius: '50%',
                  background: c2.contredite ? '#fff' : '#7fc4a3',
                  border: `2px solid ${c2.contredite ? '#c3ccd4' : '#1a4f3a'}`,
                  opacity: c2.contredite ? 0.55 : 1,
                  boxSizing: 'border-box', zIndex: 6 }} />
            ))}
          </div>

          <div style={{ position: 'relative', height: 14, marginTop: 3 }}>
            {heuresAxe.map((h) => (
              <span key={`h-${h}`} style={{ position: 'absolute', left: `${pctL(h)}%`,
                transform: 'translateX(-50%)', fontSize: 10, color: C.muet,
                fontFamily: DS.fontBody, whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums' }}>
                {formaterHeureCourte(h)}
              </span>
            ))}
            {etiquettesAxe.map((e) => (
              <span key={`l-${e.cle}`} data-testid={`horaires-libelle-${e.cle}`}
                style={{ position: 'absolute', left: `${pctL(e.min)}%`,
                  transform: pctL(e.min) > 92 ? 'translateX(-100%)'
                    : pctL(e.min) < 8 ? 'none' : 'translateX(-50%)',
                  fontSize: e.vert || e.barre ? 9.5 : 10,
                  fontWeight: e.vert ? 700 : 400,
                  color: e.vert ? '#1f5a43' : (e.barre ? '#a8b2bc' : '#6b7784'),
                  textDecoration: e.barre ? 'line-through' : 'none',
                  whiteSpace: 'nowrap', fontFamily: DS.fontBody }}>
                {e.texte}
              </span>
            ))}
          {cercles.length > 0 && (
              <span data-testid="horaires-legende-annonces"
                style={{ position: 'absolute', right: 0, top: 14, fontSize: 9.5,
                  color: C.muet, fontFamily: DS.fontBody }}>
                {/* AU DOIGT, LE SENS S'ÉCRIT (cadrage mobile 2026-08-22). */}
                {cercles.some((c2) => c2.contredite)
                  ? 'cercle barré = annonce contredite par la demande'
                  : 'cercle vert = heure annoncée par le voyageur'}
              </span>
            )}
            </div>
      </div>
    </div>
  )
}


function PastillesDemande({
  demande, contrat, seuilMin, decisions, autresDemandes, valeur, refusee,
  onChoisir, fige, bornes, sansEntete = false,
}) {
  const { pastilles } = heuresPastilles({ demande, contrat })
  const nom = demande.voyageur || 'Voyageur'
  const part = demande.event_kind === LATE
  // « Autre » — TOUJOURS là (« je ne dois jamais être coincé par la liste »).
  // Les minutes y sont possibles : c'est le cas exceptionnel — et le serveur
  // facture à l'heure ENTAMÉE (billableHours, ceil) : 13h37 accordé = 3 h.
  const [autreOuvert, setAutreOuvert] = useState(false)
  const horsGrille = !refusee && Number.isFinite(valeur)
    && !pastilles.some((pst) => pst.heure === valeur)

  // « CETTE HEURE TIENT-ELLE ? » — le verdict, appliqué à la pastille,
  // contre les FAITS SEULS (contrat + actées + annonces) : une demande non
  // décidée ne contraint pas (règle Dimitri 2026-08-16).
  const tient = (h) => {
    const d2 = { ...decisions, [demande.id]: { ...decisions[demande.id], heure: h, refusee: false } }
    const s = simuler({ contrat, demandes: autresDemandes, decisions: d2 })
    const v = verdict(s ? s.restantMin : 0, seuilMin)
    return v === 'suffisant' || v === 'juste'
  }


  const versHHMM = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
  const depuisHHMM = (s) => {
    const t2 = normaliserHeure(s)
    if (!t2) return null
    const [h, m2] = t2.split(':').map(Number)
    return h * 60 + m2
  }

  return (
    <div style={{ marginTop: 8 }}>
      {!sansEntete && (
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 5 }}>
        {part
          ? <LogOut size={14} strokeWidth={2.2} color={C.texteDoux} style={{ flexShrink: 0, alignSelf: 'center' }} />
          : <LogIn size={14} strokeWidth={2.2} color={C.texteDoux} style={{ flexShrink: 0, alignSelf: 'center' }} />}
        <span style={{ fontSize: 11.5, fontWeight: 700, color: C.gris, fontFamily: DS.fontBody,
          minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {nom} {part ? 'veut partir à' : 'veut arriver à'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: C.muet, fontFamily: DS.fontBody,
          flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
          demande {heureCourteDeHHMM(demande.requested_time)} · contrat {heureCourteDeHHMM(demande.standard_time)}
        </span>
      </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {pastilles.map((pst) => {
          // ⚠️ QUE DES HEURES ACCORDÉES (l'heure du contrat n'est pas une
          // pastille : l'accorder serait un refus, et « Refuser » existe).
          const choisie = !refusee && !autreOuvert && valeur === pst.heure
          const possible = tient(pst.heure)
          return (
            <button key={pst.heure} type="button"
              data-testid={`horaires-pastille-${demande.id}-${pst.heure}`}
              onClick={() => { setAutreOuvert(false); onChoisir(pst.heure) }}
              disabled={fige || !possible}
              aria-pressed={choisie}
              className="fbz-press"
              style={{ flex: 1, minWidth: 62, minHeight: 44, padding: '8px 11px',
                background: choisie ? '#f4f9fd' : '#fff',
                border: choisie ? '2px solid #3d5166' : '1px solid #d5e3ee',
                borderRadius: 10, cursor: (fige || !possible) ? 'default' : 'pointer',
                opacity: possible ? 1 : .38, fontFamily: DS.fontBody, textAlign: 'center' }}>
              <span style={{ display: 'block', fontSize: 13, color: '#233240', fontWeight: choisie ? 700 : 500,
                fontVariantNumeric: 'tabular-nums' }}>
                {formaterHeureCourte(pst.heure)}
              </span>
              {pst.tag === 'demande' && (
                <span style={{ display: 'block', fontSize: 9, marginTop: 1, color: '#5c8ab8',
                  fontWeight: 700, letterSpacing: '.02em' }}>
                  demandé
                </span>
              )}
            </button>
          )
        })}
        <span style={{ position: 'relative', flex: 1, minWidth: 62, display: 'flex' }}>
        <button type="button" data-testid={`horaires-autre-heure-${demande.id}`}
          onClick={() => setAutreOuvert((v) => !v)} disabled={fige}
          aria-pressed={autreOuvert || horsGrille}
          className="fbz-press"
          style={{ flex: 1, minWidth: 62, minHeight: 44, padding: '8px 11px',
            background: (autreOuvert || horsGrille) ? '#f4f9fd' : '#fff',
            border: (autreOuvert || horsGrille) ? '2px solid #3d5166' : '1px dashed #c3d5e4',
            borderRadius: 10, cursor: 'pointer', fontFamily: DS.fontBody,
            fontSize: 11, color: C.texteDoux, textAlign: 'center' }}>
          {horsGrille && !autreOuvert ? (
            <span style={{ display: 'block', fontSize: 13, color: '#233240', fontWeight: 700,
              fontVariantNumeric: 'tabular-nums' }}>{formaterHeureCourte(valeur)}</span>
          ) : 'Autre'}
        </button>
        {autreOuvert && !fige && (
          <MenuCreneaux demandeId={demande.id} bornes={bornes} valeur={valeur}
            onChoisir={(m) => { onChoisir(m); setAutreOuvert(false) }}
            onFermer={() => setAutreOuvert(false)} />
        )}
        </span>
      </div>
    </div>
  )
}

// LE MENU DES CRÉNEAUX « Autre » (Dimitri 2026-08-17) : s'ouvre SOUS la
// pastille, aligné sur elle — même règle que tous les menus : mesuré (jamais
// deviné), retourné vers le haut si pas de place, recalé à gauche s'il
// déborde à droite ; Échap + clic dehors le ferment. Des créneaux de 30 min,
// rien d'autre.
function MenuCreneaux({ demandeId, bornes, valeur, onChoisir, onFermer }) {
  const ref = useRef(null)
  const [placement, setPlacement] = useState({ haut: false, droite: false })
  useEchapPourFermer(onFermer)
  useEffect(() => {
    const surPointerDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onFermer()
    }
    const timer = setTimeout(() => document.addEventListener('pointerdown', surPointerDown, true), 0)
    return () => { clearTimeout(timer); document.removeEventListener('pointerdown', surPointerDown, true) }
  }, [onFermer])
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setPlacement({ haut: false, droite: false })
    const r = el.getBoundingClientRect()
    const carte = el.closest('[data-testid^="horaires-journee-"]')
    const limiteBas = carte ? carte.getBoundingClientRect().bottom : window.innerHeight
    const limiteDroite = carte ? carte.getBoundingClientRect().right : window.innerWidth
    setPlacement({
      haut: r.bottom > limiteBas - 8 && r.top - r.height - 8 > 0,
      droite: r.right > limiteDroite - 8,
    })
  }, [demandeId])
  const creneaux = []
  for (let m = Math.ceil(bornes.min / 30) * 30; m <= bornes.max; m += 30) creneaux.push(m)
  return (
    <div ref={ref} data-testid={`horaires-autre-champ-${demandeId}`} role="listbox"
      style={{ position: 'absolute', zIndex: 40,
        top: placement.haut ? undefined : 'calc(100% + 4px)',
        bottom: placement.haut ? 'calc(100% + 4px)' : undefined,
        ...(placement.droite ? { right: 0 } : { left: 0 }),
        minWidth: '100%', background: '#fff', border: `1px solid ${C.bord}`,
        borderRadius: 9, boxShadow: '0 6px 20px rgba(33,83,135,.18)', padding: 5,
        display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 240, overflowY: 'auto' }}>
      {creneaux.map((m) => (
        <button key={m} type="button" role="option" aria-selected={valeur === m}
          data-testid={`horaires-creneau-${demandeId}-${m}`}
          onClick={() => onChoisir(m)}
          className="fbz-press"
          style={{ border: 'none', borderRadius: 7, padding: '0 12px', minHeight: 44,
            background: valeur === m ? '#f4f9fd' : 'none', cursor: 'pointer',
            fontSize: 13, fontFamily: DS.fontBody, color: '#233240', textAlign: 'left',
            fontVariantNumeric: 'tabular-nums', fontWeight: valeur === m ? 700 : 500 }}>
          {formaterHeureCourte(m)}
        </button>
      ))}
    </div>
  )
}

// « enchaîne au Havre de Bruguières » : la contraction suit l'article du nom.
function enchaineA(nom) {
  if (!nom) return 'dans un autre logement'
  if (nom === 'un autre logement') return 'dans un autre logement'
  if (/^Le\s/.test(nom)) return `au ${nom.slice(3)}`
  if (/^La\s/.test(nom)) return `à la ${nom.slice(3)}`
  if (/^L'/.test(nom)) return `à l'${nom.slice(2)}`
  if (/^Les\s/.test(nom)) return `aux ${nom.slice(4)}`
  return `à ${nom}`
}

// ── LES FAITS D'UNE JOURNÉE (règle Dimitri 2026-08-16) ─────────────────────
// ⚠️ UNE DEMANDE NON DÉCIDÉE NE CONTRAINT PAS. Chaque demande se juge contre
// les FAITS : le contrat, resserré par les demandes DÉJÀ ACCORDÉES
// (granted_context — le refus serveur, lui, fait disparaître la demande et
// rend son côté au contrat), et déplacé par les ANNONCES du formulaire — une
// annonce est un fait, pas une demande. Une annonce contredite par une
// demande du même côté ne vaut plus : le fait retombe sur le contrat.
function faitsJournee({ contratBase, actees, pendantes, annoncesRun }) {
  if (!contratBase) return null
  const annDep = annoncesRun?.departure ?? null
  const annArr = annoncesRun?.arrival ?? null
  const depFavorable = annDep != null && annDep < contratBase.debut
  const arrFavorable = annArr != null && annArr > contratBase.fin
  // CAS 5 (règle des repères 2026-08-16) : une annonce du même côté qu'un
  // ACCORDÉ (payé) ne fait pas foi — on ne la dessine pas du tout. Une
  // annonce contredite par une PENDANTE garde son bandeau ambre (validé).
  const lateActee = actees.some((d) => d.event_kind === LATE)
  const earlyActee = actees.some((d) => d.event_kind === EARLY)
  const depEcrasee = depFavorable && lateActee
  const arrEcrasee = arrFavorable && earlyActee
  const depContredite = depFavorable && !depEcrasee && pendantes.some((d) => d.event_kind === LATE)
  const arrContredite = arrFavorable && !arrEcrasee && pendantes.some((d) => d.event_kind === EARLY)

  let debut = depFavorable && !depContredite && !depEcrasee ? annDep : contratBase.debut
  let fin = arrFavorable && !arrContredite && !arrEcrasee ? annArr : contratBase.fin
  for (const a of actees) {
    const t = normaliserHeure(a.granted_time)
    if (!t) continue
    const [h, m] = t.split(':').map(Number)
    const min = h * 60 + m
    if (a.event_kind === LATE) debut = Math.max(debut, min)
    else fin = Math.min(fin, min)
  }
  return {
    debut, fin,
    annDep, annArr,
    depFavorable: depFavorable && !depEcrasee,
    arrFavorable: arrFavorable && !arrEcrasee,
    depContredite, arrContredite,
  }
}

// ── LA CARTE D'UNE JOURNÉE — une carte, N rangées de décision ───────────────
// (retour à la carte unique, décision Dimitri 2026-08-16 : les deux demandes
// vivent ensemble, chaque RANGÉE décide seule — jamais « valider les deux ».)
function JourneeCartes({
  journee, logement, run, annoncesRun, grantedContext, nowMin, estAdmin, onNavigate,
  onValider, onRefuserTout, busy, lectureSeule, autresMenages,
}) {
  const contratBase = useMemo(() => bornesContrat(logement), [logement])
  // ⚠️ LE RÉFÉRENTIEL EST LE MÉNAGE REGARDÉ, PAS LA FENÊTRE (2e constat
  // Dimitri 2026-08-17) — hook AVANT tout retour anticipé (rules-of-hooks :
  // l'overlay du dev-server bloquait TOUTE la page, d'où « rien ne bouge »).
  const heureFixeeMin = useMemo(() => {
    const hf = normaliserHeure(run?.heure_fixee)
    if (!hf) return null
    const [h, m] = hf.split(':').map(Number)
    return h * 60 + m
  }, [run])
  const seuil = useMemo(() => seuilMenage(run, logement), [run, logement])
  const actees = useMemo(
    () => (grantedContext || []).filter(
      (g) => g.logement_id === journee.logementId && g.event_date === journee.date),
    [grantedContext, journee]
  )
  // ⚠️ AFFICHAGE SEUL (consigne Dimitri 2026-08-19) : une demande PROPOSÉE se
  // montre à l'orchestrateur comme une décision ORDINAIRE — « Accordé · 15h »,
  // « Refusé » — sans un mot sur la mécanique de confirmation admin. Le
  // serveur, lui, ne bouge pas : le sous-état existe, l'admin le voit.
  const proposees = (journee.demandes || []).filter((d) => d.proposed_at)
  const pendantes = (journee.demandes || []).filter((d) => !d.proposed_at)
  const faits = useMemo(
    () => faitsJournee({ contratBase, actees, pendantes, annoncesRun }),
    [contratBase, actees, pendantes, annoncesRun]
  )
  // Le contrat de CALCUL d'une demande, ce sont les faits.
  const contrat = useMemo(
    () => (faits ? { debut: faits.debut, fin: faits.fin } : null), [faits])

  // ⚠️ « COMMENCÉ » N'EXISTE QU'AUJOURD'HUI : l'heure courante appliquée à une
  // journée FUTURE dirait « Sofia a commencé » sur un ménage de demain.
  const nowMinIci = journee.date === todayParisIso() ? nowMin : null

  const [decisions, setDecisions] = useState(() => {
    const out = {}
    for (const d of pendantes) {
      const b = bornesCurseur({ demande: d, contrat: contrat || contratBase, seuilMin: seuil })
      out[d.id] = { heure: heureInitiale(d, b), refusee: false }
    }
    return out
  })
  // Le seuil (exception du run), les actées et les annonces arrivent APRÈS
  // coup : les décisions stockées se re-bornent (leçon du 2026-08-11).
  useEffect(() => {
    if (!contrat) return
    setDecisions((prev) => {
      const next = { ...prev }
      let change = false
      for (const d of pendantes) {
        const courant = prev[d.id]
        if (!courant) continue
        const b = bornesCurseur({ demande: d, contrat, seuilMin: seuil })
        if (!b.possible) continue
        const borne = aimanterDansBornes(courant.heure, b)
        if (borne !== courant.heure) { next[d.id] = { ...courant, heure: borne }; change = true }
      }
      return change ? next : prev
    })
  }, [seuil, contrat, pendantes])

  const changer = (id, heure) =>
    setDecisions((d) => ({ ...d, [id]: { ...d[id], heure, refusee: false } }))

  const simPreview = useMemo(
    () => (contrat ? simuler({ contrat, demandes: pendantes, decisions }) : null),
    [contrat, pendantes, decisions]
  )
  const debutCommence = useMemo(
    () => menageCommence(run, { nowMin: nowMinIci, contrat, seuilMin: seuil }),
    [run, nowMinIci, contrat, seuil]
  )
  const agent = run?.assigned_provider_email || null
  const prenomAgent = agent ? (firstNameFromEmail(agent) || agent) : null

  if (!contratBase) {
    return (
      <div data-testid={`horaires-journee-${journee.cle}`}
        style={{ background: '#fff', border: `1px solid ${C.bord}`, borderRadius: 13,
          padding: '14px 17px', marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#2e3d4d', fontFamily: DS.fontDisplay }}>
          {logement?.nom || journee.logementId}
        </div>
        <div data-testid="horaires-sans-horaires"
          style={{ marginTop: 8, padding: '10px 12px', background: C.fondAlerte,
            borderRadius: 9, fontSize: 11.5, color: C.texteAlerte, fontFamily: DS.fontBody }}>
          {/* LE MESSAGE NOMME CE QUI MANQUE (tri Dimitri 2026-08-17) : départ,
              arrivée, ou les deux — la durée a un défaut, jamais bloquante.
              Et il dit OÙ : la fiche du logement (bouton direct pour l'admin ;
              l'orchestrateur, lui, n'a pas la main sur la fiche). */}
          {(() => {
            const manqueDep = logement?.checkout == null
            const manqueArr = logement?.checkin == null
            const quoi = manqueDep && manqueArr
              ? "l'heure de départ et l'heure d'arrivée du contrat"
              : manqueDep ? "l'heure de départ du contrat" : "l'heure d'arrivée du contrat"
            return <>Il manque {quoi} pour ce logement — impossible de calculer
              le temps de ménage. {estAdmin
                ? <>Renseignez-{manqueDep && manqueArr ? 'les' : 'la'} dans la fiche du logement avant de décider.</>
                : <>Elle se renseigne dans la fiche du logement (administrateur).</>}
            </>
          })()}
          {estAdmin && onNavigate && (
            <button type="button" data-testid="horaires-ouvrir-fiche"
              onClick={() => onNavigate('logements')}
              style={{ display: 'block', marginTop: 8, background: '#fff',
                border: `1px solid ${C.texteAlerte}`, color: C.texteAlerte,
                borderRadius: 7, padding: '6px 11px', fontSize: 11.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: DS.fontBody }}>
              Ouvrir la fiche du logement
            </button>
          )}
        </div>
      </div>
    )
  }



  // ── LE JUGEMENT, PAR DEMANDE, CONTRE LES FAITS SEULS ──
  const simDe = (d, heure) => simuler({
    contrat, demandes: [d],
    decisions: { [d.id]: { heure, refusee: false } },
  })
  const verdictDe = (d, heure) => {
    const s = simDe(d, heure)
    return verdict(s ? s.restantMin : 0, seuil)
  }
  const tientPastille = (d, h) => {
    const v = verdictDe(d, h)
    return v === 'suffisant' || v === 'juste'
  }

  const parDemande = pendantes.map((d) => {
    const dec = decisions[d.id]
    const b = bornesCurseur({ demande: d, contrat, seuilMin: seuil })
    const sel = dec?.heure ?? b.min
    const v = verdictDe(d, sel)
    const { pastilles } = heuresPastilles({ demande: d, contrat })
    const rienNeTient = pastilles.length > 0 && pastilles.every((p) => !tientPastille(d, p.heure))
    const premiereFermee = pastilles.find((p) => !tientPastille(d, p.heure)) || null
    return { d, dec, b, sel, v, bon: v === 'suffisant' || v === 'juste', pastilles, rienNeTient, premiereFermee }
  })

  const rouge = parDemande.some((x) => x.rienNeTient)

  // ── LA FRISE : LES FAITS + la PREVIEW cumulée des heures sélectionnées.
  // Le bloc dit « ce qui resterait si tout était accordé tel quel » — une
  // visualisation ; le JUGEMENT de chaque rangée, lui, ne croise jamais.

  const requisMin = (d) => {
    const t = normaliserHeure(d.requested_time)
    if (!t) return null
    const [h, m] = t.split(':').map(Number)
    return h * 60 + m
  }
  // Les hachures bleues : ce que CHAQUE pendante réclame (jamais l'heure
  // sélectionnée), des deux côtés dans la même frise.
  const hachures = pendantes.map((d) => {
    const r = requisMin(d)
    if (r == null) return null
    const z = d.event_kind === LATE
      ? { debut: contratBase.debut, fin: r, late: true }
      : { debut: r, fin: contratBase.fin, late: false }
    if (z.fin <= z.debut) return null
    // ⚠️ LES HACHURES SUIVENT MON CHOIX (Dimitri 2026-08-17, annule la règle
    // précédente) : elles montrent CE QUE J'ACCORDE — le réclamé, lui, se lit
    // sur la pastille « demandé ». Refuser → plus de hachures du tout.
    // (Au Suivi, aucune décision n'est en cours : les hachures y montrent la
    // demande entière — même sémantique : « le temps que le voyageur va
    // obtenir », au maximum tant que rien n'est tranché.)
    const dec = decisions[d.id]
    if (dec) {
      if (dec.refusee) return null
      if (Number.isFinite(dec.heure)) {
        if (d.event_kind === LATE) z.fin = Math.min(dec.heure, z.fin)
        else z.debut = Math.max(dec.heure, z.debut)
        if (z.fin <= z.debut) return null
      }
    }
    return { ...z, cle: d.id }
  }).filter(Boolean)

  const prenomPartant = ([...pendantes, ...actees].find((d) => d.event_kind === LATE))?.voyageur || null
  const arrivantSource = [...pendantes, ...actees].find((d) => d.event_kind === EARLY) || null
  const prenomArrivant = arrivantSource?.voyageur || null
  const arrivantConnu = !!arrivantSource || faits.arrFavorable

  const candidats = [contratBase.debut, contratBase.fin, faits.debut, faits.fin,
    ...(simPreview ? [simPreview.debut, simPreview.fin] : []),
    ...(faits.annDep != null ? [faits.annDep] : []),
    ...(faits.annArr != null ? [faits.annArr] : []),
    ...pendantes.map(requisMin).filter(Number.isFinite),
  ].filter(Number.isFinite)
  // 15 min de respiration : un cercle d'annonce pile sur la borne serait coupé.
  const axe = { debut: Math.min(...candidats) - 15, fin: Math.max(...candidats) + 15 }

  // RÈGLE DES REPÈRES (2026-08-16) : LA CARTE PORTE L'HEURE QUI FAIT FOI.
  // Un trait + « contrat Xh » n'apparaissent QUE quand une bulle verte existe
  // (le réel diffère du contrat). Partout ailleurs : rien.
  const cercles = []
  const etiquettes = []
  if (faits.depFavorable || faits.depContredite) {
    etiquettes.push({ cle: 'depart', min: contratBase.debut, trait: true,
      texte: `contrat ${formaterHeureCourte(contratBase.debut)}` })
  }
  if (faits.arrFavorable || faits.arrContredite) {
    etiquettes.push({ cle: 'arrivee', min: contratBase.fin, trait: true,
      texte: `contrat ${formaterHeureCourte(contratBase.fin)}` })
  }
  if (faits.depFavorable) {
    cercles.push({ cle: 'dep', min: faits.annDep, contredite: faits.depContredite })
    etiquettes.push(faits.depContredite
      ? { cle: 'annonce-dep', min: faits.annDep, barre: true, texte: formaterHeureCourte(faits.annDep) }
      : { cle: 'annonce-dep', min: faits.annDep, vert: true, texte: `${formaterHeureCourte(faits.annDep)} annoncé` })
  }
  if (faits.arrFavorable) {
    cercles.push({ cle: 'arr', min: faits.annArr, contredite: faits.arrContredite })
    etiquettes.push(faits.arrContredite
      ? { cle: 'annonce-arr', min: faits.annArr, barre: true, texte: formaterHeureCourte(faits.annArr) }
      : { cle: 'annonce-arr', min: faits.annArr, vert: true, texte: `${formaterHeureCourte(faits.annArr)} annoncé` })
  }

  // ── LES RAISONS — une raison = un point, sous les rangées. ──
  const menageDebutSim = simPreview
    ? Math.max(Number.isFinite(heureFixeeMin) ? heureFixeeMin : simPreview.debut, simPreview.debut)
    : null
  const conflitsAilleurs = simPreview
    ? autresMenages.filter((a) => a.fin > menageDebutSim && a.debut < menageDebutSim + seuil)
    : []
  const raisons = []
  for (const x of parDemande) {
    if (!x.premiereFermee) continue
    const s = simDe(x.d, x.premiereFermee.heure)
    const reste = formaterDuree(Math.max(0, s ? s.restantMin : 0))
    raisons.push({
      cle: `temps-${x.d.id}`, testId: `horaires-pourquoi-ferme-${x.d.id}`,
      // « il lui en faut » : impersonnel — jamais d'accord de genre.
      texte: prenomAgent
        ? `${formaterHeureCourte(x.premiereFermee.heure)} ne laisserait que ${reste} à ${prenomAgent}, il lui en faut ${formaterDuree(seuil)}.`
        : `${formaterHeureCourte(x.premiereFermee.heure)} ne laisserait que ${reste} — le ménage en demande ${formaterDuree(seuil)}.`,
    })
  }
  for (const a of conflitsAilleurs) {
    // ⚠️ LE SENS DE LA PHRASE (constat Dimitri 2026-08-17) : le détecteur est
    // un chevauchement PUR — il attrape l'autre ménage qu'il soit AVANT ou
    // APRÈS. « Enchaîner » veut dire après : quand l'autre commence avant la
    // fenêtre regardée, il en VIENT et c'est sa FIN qui mord.
    const apres = a.debut >= menageDebutSim
    raisons.push({
      cle: `ailleurs-${a.id}`, testId: `horaires-conflit-ailleurs-${a.id}`,
      // ⚠️ LA PLAGE COMPLÈTE (Dimitri 2026-08-17) : le début ET la fin —
      // sans la fin, impossible de juger si ça coince. La fin est toujours
      // calculable (durée du ménage > défaut logement > 90 min) ; un ménage
      // SANS DÉBUT connu est écarté de la détection en amont (jamais à tort).
      texte: apres
        ? `${a.prenom} enchaîne ${enchaineA(a.nom)} de ${formaterHeureCourte(a.debut)} à ${formaterHeureCourte(a.fin)}.`
        : `${a.prenom} est ${enchaineA(a.nom)} de ${formaterHeureCourte(a.debut)} à ${formaterHeureCourte(a.fin)}.`,
    })
  }

  const total = pendantes.length + actees.length
  // Quand la seule demande du jour est une PROPOSITION en attente, pendantes
  // est vide — la phrase disait « veut arriver à — ». La proposée prend le relais.
  const demandeEnTete = pendantes[0] || proposees[0]
  const late0 = demandeEnTete?.event_kind === LATE
  const contradiction = faits.depContredite || arrContediteSafe(faits)
  const bordure = rouge ? '#e0b5a5' : contradiction ? '#e0cba5' : '#e3e8ee'
  const degradeColonne = rouge
    ? 'linear-gradient(160deg, #a63b21, #d4714f)'
    : 'linear-gradient(160deg, #5c8ab8, #87b8e6)'
  const gainAnnonce = faits.depFavorable && !faits.depContredite ? contratBase.debut - faits.annDep
    : faits.arrFavorable && !faits.arrContredite ? faits.annArr - contratBase.fin : 0
  const annonceCote = faits.depFavorable && !faits.depContredite ? 'dep'
    : faits.arrFavorable && !faits.arrContredite ? 'arr' : null
  const annonceQui = annonceCote === 'dep' ? prenomPartant : prenomArrivant
  const auMoinsUnBon = parDemande.some((x) => x.bon && !x.dec?.refusee)
  const cerclePx = total > 1 ? 30 : 32
  const contreditePendante = pendantes.find((d) =>
    (faits.depContredite && d.event_kind === LATE) || (faits.arrContredite && d.event_kind === EARLY)) || null
  const plusVieille = pendantes.reduce((m, d) =>
    (Date.parse(d.created_at) || Infinity) < (Date.parse(m?.created_at) || Infinity) ? d : m, pendantes[0])

  return (
    <div data-testid={`horaires-journee-${journee.cle}`}
      style={{ display: 'flex', background: '#fff', border: `1px solid ${bordure}`,
        borderRadius: 13, overflow: 'hidden', marginBottom: 14 }}>
      {/* ═══ LA COLONNE — une pastille par demande, coche verte quand décidée ═══ */}
      <div aria-hidden="true" data-testid={`horaires-colonne-${journee.cle}`}
        style={{ width: 52, flexShrink: 0, background: degradeColonne,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '14px 0', gap: 6 }}>
        {actees.map((a) => (
          <span key={a.id} style={{ width: cerclePx, height: cerclePx, borderRadius: '50%',
            background: 'rgba(255,255,255,.92)', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center' }}>
            <CircleCheck size={16} strokeWidth={2.4} color="#1f5a43" />
          </span>
        ))}
        {pendantes.map((d) => (
          <span key={d.id} style={{ width: cerclePx, height: cerclePx, borderRadius: '50%',
            background: 'rgba(255,255,255,.92)', color: rouge ? '#8f2f1d' : '#3d5166',
            fontSize: 13.5, fontWeight: 700, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', fontFamily: DS.fontBody }}>
            {String(d.voyageur || 'V').charAt(0).toUpperCase()}
          </span>
        ))}
        {total === 1 && (late0
          ? <LogOut size={15} strokeWidth={2.2} color="rgba(255,255,255,.9)" />
          : <LogIn size={15} strokeWidth={2.2} color="rgba(255,255,255,.9)" />)}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* ═══ a) L'EN-TÊTE ═══ */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '14px 17px 13px' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 17, color: '#2e3d4d', lineHeight: 1.2,
              fontWeight: 600, fontFamily: DS.fontDisplay }}>
              {logement?.nom || journee.logementId}
            </div>
            {/* MULTI-LIGNES (2026-08-17) : la phrase porte l'explication —
                elle se lit EN ENTIER, à 390px comme ailleurs. */}
            <div data-testid={`horaires-phrase-${journee.cle}`}
              style={{ fontSize: 12.5, color: '#5c6773', marginTop: 2,
                fontFamily: DS.fontBody, lineHeight: 1.35 }}>
              {total > 1
                ? `${total === 2 ? 'Deux' : total} demandes · ${formatDateFr(journee.date)}`
                : `${demandeEnTete?.voyageur || 'Le voyageur'} veut ${late0 ? 'partir' : 'arriver'} à ${heureCourteDeHHMM(demandeEnTete?.requested_time)} · ${formatDateFr(journee.date)}`}
            </div>
          </div>
          <span style={{ flexShrink: 0, fontSize: 10.5, color: '#98a2ad', fontFamily: DS.fontBody }}>
            {ancienneteDemande(plusVieille, nowMin)}
          </span>
        </div>

        {/* ═══ LE BANDEAU DE CONTRADICTION (le violet a disparu : les demandes
            vivent dans la carte, plus besoin de s'annoncer) ═══ */}
        {contradiction && contreditePendante && (
          <div data-testid={`horaires-contradiction-${contreditePendante.id}`}
            style={{ margin: '0 17px 10px', display: 'flex', alignItems: 'center', gap: 7,
              background: '#fdf7ee', border: '1px solid #e8d4b0', borderRadius: 8,
              padding: '7px 11px' }}>
            <RefreshCwOff size={15} strokeWidth={2.2} color="#9a6a1c" style={{ flexShrink: 0 }} />
            {/* Jamais d'accord de genre sur un prénom réel : « mais veut ». */}
            <span style={{ fontSize: 11, color: '#8a5f19', fontFamily: DS.fontBody }}>
              {contreditePendante.voyageur || 'Le voyageur'} avait annoncé{' '}
              {formaterHeureCourte(contreditePendante.event_kind === LATE ? faits.annDep : faits.annArr)},
              {' '}mais veut finalement {contreditePendante.event_kind === LATE ? 'partir' : 'venir'} à{' '}
              {heureCourteDeHHMM(contreditePendante.requested_time)}.
            </span>
          </div>
        )}

        {/* ═══ b) LA FRISE — les faits + la preview des heures choisies ═══ */}
        <div style={{ padding: '0 17px 13px' }}>
          {simPreview && (
            <FrisePeinture
              axe={axe} sim={simPreview} seuilMin={seuil} agent={agent}
              menageDebutMin={heureFixeeMin}
              commence={debutCommence.commence} nowMin={nowMinIci}
              refDepart={faits.debut} refArrivee={faits.fin}
              arrivantConnu={arrivantConnu}
              prenomPartant={prenomPartant} prenomArrivant={prenomArrivant}
              personnesPartant={Number(run?.guest_count) || null}
              personnesArrivant={annoncesRun?.arrivantCount ?? null}
              hachures={hachures}
              cercles={cercles} etiquettesAxe={etiquettes}
              hauteur={total > 1 ? 38 : HAUTEUR_PISTE_FRISE}
              testId={`horaires-frise-${journee.cle}`} />
          )}

          {/* L'encart de verdict SEULEMENT quand le bloc seul ne suffit pas :
              sans bloc du tout (plus aucun temps), il est le seul témoin. */}
          {simPreview && simPreview.restantMin <= 0 && (
            <div data-testid={`horaires-verdict-${journee.cle}`}
              style={{ marginTop: 10, display: 'flex', gap: 7, alignItems: 'center',
                background: C.fondAlerte, borderRadius: 9, padding: '9px 12px' }}>
              <AlertTriangle size={15} strokeWidth={2.2} color={C.texteAlerte} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 11.5, color: C.texteAlerte, fontWeight: 700, fontFamily: DS.fontBody }}>
                Plus aucun temps de ménage — il en faut {formaterDuree(seuil)}
              </span>
            </div>
          )}
          {/* LE CAS LIMITE (ambre) : une rangée tiendrait PILE. Avertissement,
              jamais un refus — pastilles et bouton restent ouverts. */}
          {parDemande.filter((x) => x.v === 'juste').map((x) => (
            <div key={`lim-${x.d.id}`} data-testid={`horaires-limite-${x.d.id}`}
              style={{ marginTop: 10, display: 'flex', gap: 7, alignItems: 'center',
                background: '#fdf7ee', borderRadius: 9, padding: '9px 12px' }}>
              <AlertCircle size={15} strokeWidth={2.2} color="#9a6a1c" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 11.5, color: '#8a5f19', fontFamily: DS.fontBody }}>
                {prenomAgent || "L'agent"} finirait pile à l'arrivée
                {prenomArrivant ? ` de ${prenomArrivant}` : ''}. Aucune marge si le ménage déborde.
              </span>
            </div>
          ))}
          {gainAnnonce > 0 && (
            <div data-testid={`horaires-annonce-encart-${journee.cle}`}
              style={{ marginTop: 10, display: 'flex', gap: 7, alignItems: 'center',
                background: C.vertFond, borderRadius: 9, padding: '9px 12px' }}>
              <CircleCheck size={15} strokeWidth={2.2} color={C.vertTexte} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 11.5, color: C.vertTexte, fontFamily: DS.fontBody }}>
                {annonceQui || 'Le voyageur'} annonce {annonceCote === 'dep' ? 'partir' : 'arriver'} à{' '}
                {formaterHeureCourte(annonceCote === 'dep' ? faits.annDep : faits.annArr)} au lieu de{' '}
                {formaterHeureCourte(annonceCote === 'dep' ? contratBase.debut : contratBase.fin)} —{' '}
                {prenomAgent || "l'agent"} gagne {formaterDuree(gainAnnonce)}.
              </span>
            </div>
          )}
          {agent && auMoinsUnBon && !lectureSeule && !debutCommence.commence && (
            <div data-testid="horaires-notif-agent"
              style={{ marginTop: 10, padding: '8px 12px', background: C.notifFond,
                borderRadius: 9, display: 'flex', gap: 7, alignItems: 'center' }}>
              <Bell size={13} strokeWidth={2.2} color={C.notifIcone} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 10.5, color: C.notifTexte, fontFamily: DS.fontBody }}>
                Le créneau de {prenomAgent} est mis à jour.
              </span>
            </div>
          )}
          {debutCommence.commence && (
            <div data-testid="horaires-avertissement-commence"
              style={{ marginTop: 10, padding: '9px 12px', background: C.fondAlerte,
                borderRadius: 9, display: 'flex', gap: 7, alignItems: 'flex-start' }}>
              <AlertTriangle size={13} strokeWidth={2.2} color={C.texteAlerte}
                style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 11, color: C.texteAlerte, fontFamily: DS.fontBody, lineHeight: 1.5 }}>
                <strong>
                  {(firstNameFromEmail(debutCommence.agent) || debutCommence.agent)} a commencé
                  à {formaterHeureCourte(debutCommence.debutMin)} — il est {formaterHeureCourte(nowMinIci)}.
                </strong>
                <br />
                Accorder l'obligerait à s'interrompre et à revenir.
              </span>
            </div>
          )}
        </div>

        {/* ═══ c) LES RANGÉES DE DÉCISION — chaque rangée décide SEULE ═══ */}
        <div style={{ background: '#e8f2fa' }}>
          {[...actees,
            ...proposees.map((d) => ({
              ...d,
              granted_time: d.proposed_kind === 'refuse' ? null : d.proposed_time,
              __refusDecide: d.proposed_kind === 'refuse',
            }))].map((a, i) => (
            <div key={a.id} data-testid={`horaires-rangee-actee-${a.id}`}
              style={{ display: 'flex', alignItems: 'center', gap: 7,
                padding: '11px 17px', background: '#f4f8f6',
                borderTop: i > 0 ? '1px solid #dfe9e4' : 'none' }}>
              <CircleCheck size={15} strokeWidth={2.4} color="#1f5a43" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#1f5a43', fontFamily: DS.fontBody }}>
                {a.__refusDecide
                  ? `${a.voyageur || 'Le voyageur'} garde son horaire de contrat — refusé`
                  : `${a.voyageur || 'Le voyageur'} ${a.event_kind === LATE ? 'partira' : 'arrivera'} à ${heureCourteDeHHMM(a.granted_time)}`}
              </span>
              {/* ⚠️ Pas de « Revenir » : l'accord est un geste d'argent ONE-SHOT
                  (verrou validated_at + capture Stripe partielle). Signalé. */}
            </div>
          ))}
          {parDemande.map((x, i) => (
            <div key={x.d.id}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 9, flexWrap: 'wrap',
                padding: '11px 17px',
                borderTop: (i > 0 || actees.length > 0) ? '1px solid #d5e3ee' : 'none' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                minWidth: 104, paddingTop: 12, flexShrink: 0 }}>
                {x.d.event_kind === LATE
                  ? <LogOut size={15} strokeWidth={2.2} color="#3d5166" />
                  : <LogIn size={15} strokeWidth={2.2} color="#3d5166" />}
                <span style={{ fontSize: 11.5, fontWeight: 700, color: '#22303d', fontFamily: DS.fontBody }}>
                  {x.d.voyageur || 'Le voyageur'} {x.d.event_kind === LATE ? 'veut partir à' : 'veut arriver à'}
                </span>
              </span>
              <div style={{ flex: '1 1 220px', minWidth: 180 }}>
                <PastillesDemande demande={x.d} contrat={contrat} seuilMin={seuil}
                  decisions={decisions} autresDemandes={[x.d]}
                  valeur={x.sel} refusee={!!x.dec?.refusee}
                  onChoisir={(h) => changer(x.d.id, h)}
                  fige={lectureSeule} bornes={x.b} sansEntete />
              </div>
              {lectureSeule ? null : debutCommence.commence ? (
                <>
                  <button type="button" data-testid="horaires-principal-refuser"
                    disabled={busy} onClick={() => onRefuserTout(journee, [x.d])}
                    className="fbz-press fbz-cible-tactile"
                    style={boutonPlein(C.gris, busy)}>
                    Refuser
                  </button>
                  <button type="button" data-testid="horaires-secondaire-accorder"
                    disabled={busy || !!x.dec?.refusee}
                    onClick={() => onValider(journee, [x.d], decisions)}
                    className="fbz-press fbz-cible-tactile"
                    style={boutonSecondaire(!!x.dec?.refusee)}>
                    Accorder quand même
                  </button>
                </>
              ) : (
                <>
                  <button type="button" data-testid={`horaires-valider-${x.d.id}`}
                    disabled={busy || !x.bon || !!x.dec?.refusee}
                    className="fbz-press fbz-cible-tactile"
                    onClick={() => onValider(journee, [x.d], decisions)}
                    style={boutonPlein(x.bon && !x.dec?.refusee ? C.gris : '#c3ccd4', busy)}>
                    {!x.bon ? 'Créneau insuffisant'
                      : `Accorder ${formaterHeureCourte(x.dec?.heure ?? x.b.min)}`}
                  </button>
                  <button type="button" data-testid={`horaires-refuser-tout-${x.d.id}`}
                    disabled={busy} onClick={() => onRefuserTout(journee, [x.d])}
                    className="fbz-press fbz-cible-tactile"
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 13, color: C.gris, fontWeight: 600,
                      fontFamily: DS.fontBody, padding: '10px 8px' }}>
                    Refuser
                  </button>
                  {!x.bon && (
                    <button type="button" data-testid={`horaires-quand-meme-${x.d.id}`}
                      disabled={busy} onClick={() => onValider(journee, [x.d], decisions)}
                      className="fbz-press fbz-cible-tactile"
                      style={boutonSecondaire(false)}>
                      Accorder quand même
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
          {lectureSeule && (
            <div data-testid="horaires-lecture-seule"
              style={{ padding: '0 17px 11px', fontSize: 11, color: C.texteDoux, fontFamily: DS.fontBody }}>
              Lecture seule — « Modifier » en haut de page pour décider.
            </div>
          )}
          {/* L'EMPLACEMENT UNIQUE de la journée de l'agent (Dimitri
              2026-08-17) : conflit réel → alerte terracotta ; pas de conflit
              mais d'autres ménages → ligne grise neutre ; rien → disparaît.
              Un seul endroit à regarder. Ordre CHRONOLOGIQUE, toujours. */}
          {(raisons.length > 0 || (autresMenages.length > 0 && prenomAgent)) && (
            <div style={{ padding: '0 17px 12px' }}>
              {raisons.map((r) => (
                <div key={r.cle} data-testid={r.testId}
                  style={{ display: 'flex', gap: 7, marginBottom: 5 }}>
                  <span aria-hidden="true" style={{ width: 4, height: 4,
                    borderRadius: '50%', background: '#8f2f1d',
                    marginTop: 6, flexShrink: 0 }} />
                  <span style={{ fontSize: 10.5, color: '#8f2f1d',
                    lineHeight: 1.5, fontFamily: DS.fontBody }}>
                    {r.texte}
                  </span>
                </div>
              ))}
              {autresMenages.length > 0 && prenomAgent
                && !raisons.some((r) => String(r.cle).startsWith('ailleurs-')) && (() => {
                const chrono = [...autresMenages].sort((a, b) => a.debut - b.debut)
                return (
                  <div data-testid={`horaires-journee-agent-${journee.cle}`}
                    style={{ fontSize: 10.5, color: '#5c6773', lineHeight: 1.5,
                      fontFamily: DS.fontBody }}>
                    {chrono.length <= 3
                      ? `${prenomAgent} est aussi ${chrono
                          .map((a) => `${enchaineA(a.nom)} de ${formaterHeureCourte(a.debut)} à ${formaterHeureCourte(a.fin)}`)
                          .join(' et ')}.`
                      : `${prenomAgent} a ${chrono.length} autres ménages : ${chrono
                          .map((a) => formaterHeureCourte(a.debut)).join(', ')} — dernier fini à ${formaterHeureCourte(Math.max(...chrono.map((a) => a.fin)))}.`}
                  </div>
                )
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// (garde de lisibilité : la contradiction côté arrivée, nommée)
function arrContediteSafe(faits) {
  return !!faits.arrContredite
}

function ancienneteDemande(demande, nowMin) {
  const t = Date.parse(demande?.created_at)
  if (!Number.isFinite(t)) return 'en attente'
  const min = Math.floor((Date.now() - t) / 60000)
  if (min < 1) return "à l'instant"
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h} h`
  return `il y a ${Math.floor(h / 24)} j`
}

function boutonPlein(fond, busy) {
  return {
    background: fond, color: '#fff', border: 'none', borderRadius: DS.radiusControl,
    padding: '10px 17px', fontSize: 13, fontWeight: 700, fontFamily: DS.fontBody,
    cursor: busy ? 'wait' : (fond === '#c3ccd4' ? 'not-allowed' : 'pointer'),
    opacity: busy ? .6 : 1,
  }
}
function boutonSecondaire(estInactif) {
  return {
    background: '#fff', color: C.gris, border: `1px solid ${C.bord}`,
    borderRadius: DS.radiusControl, padding: '10px 17px', fontSize: 13, fontWeight: 600,
    fontFamily: DS.fontBody, cursor: estInactif ? 'not-allowed' : 'pointer',
    opacity: estInactif ? .5 : 1,
  }
}

// ── VUE PRINCIPALE ──────────────────────────────────────────────────────────

export default function OrchestrateurHorairesView({ basculePage = null, currentUser, destination, onAller, onDestinationConsommee, onNavigate }) {
  // ADMIN = LECTURE SEULE par défaut (geste d'argent). Le mode « Modifier » est
  // LOCAL à la vue : il ne survit pas à une navigation — on ne laisse pas un
  // écran d'argent « ouvert » en quittant la page.
  const estAdmin = currentUser?.role === 'admin'
  const [modeModif, setModeModif] = useState(false)
  const lectureSeule = estAdmin && !modeModif
  // COUCHE PROPOSITION/CONFIRMATION : côté ORCHESTRATEUR, l'écran se TAIT
  // (consigne Dimitri 2026-08-19) — il accorde, c'est tout ; une proposition
  // en file se montre comme une décision ordinaire. Le mécanisme serveur est
  // inchangé ; l'admin garde À confirmer, badge et compte à rebours.

  const [data, setData] = useState({
    pending: [], treated_today: [], logements: {}, pending_count: 0, hub_actif: false,
    service_actif: true, decision_mode: 'admin_confirms', expired: [],
  })

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const [runs, setRuns] = useState([])
  // ⚠️ LIEN 2 — ON PEUT ARRIVER SUR UNE DEMANDE PRÉCISE, pas sur la liste.
  // `cible` réduit l'écran à cette journée-là ; `retour` porte le chemin
  // inverse. Consommé UNE FOIS puis effacé.
  const [cible, setCible] = useState(null)
  const [retour, setRetour] = useState(null)
  // ⚠️ LIEN 3 — CE QUI RESTE APRÈS « ACCORDER ». Une carte qui disparaît sans
  // rien dire laisse douter que l'action a eu lieu ; et sans le bouton de
  // retour, il faut re-naviguer à la main jusqu'à la journée qu'on vient de
  // modifier — c'est-à-dire refaire le trajet qu'on venait d'épargner.
  const [confirmation, setConfirmation] = useState(null)
  const [nowHHMM, setNowHHMM] = useState(() => nowParisHHMM())

  useEffect(() => {
    const id = setInterval(() => setNowHHMM(nowParisHHMM()), 60000)
    return () => clearInterval(id)
  }, [])
  const nowMin = useMemo(() => {
    const t = normaliserHeure(nowHHMM)
    if (!t) return null
    const [h, m] = t.split(':').map(Number)
    return h * 60 + m
  }, [nowHHMM])

  useEffect(() => {
    const d = pourOnglet(destination, ONGLET_HORAIRES)
    if (!d) return
    setCible(d.demandeId || null)
    setRetour(cheminRetour(d))
    setConfirmation(null)
    onDestinationConsommee && onDestinationConsommee()
  }, [destination, onDestinationConsommee])

  const refresh = useCallback(async () => {
    setError(null)
    try {
      const json = await postAction({ action: 'services_orchestrateur_hourly_list' })
      setData({
        pending: Array.isArray(json?.pending) ? json.pending : [],
        treated_today: Array.isArray(json?.treated_today) ? json.treated_today : [],
        pending_count: Number(json?.pending_count) || 0,
        logements: json?.logements && typeof json.logements === 'object' ? json.logements : {},
        granted_context: Array.isArray(json?.granted_context) ? json.granted_context : [],
        hub_actif: json?.hub_actif === true,
        // `false` UNIQUEMENT si le serveur l'affirme : un serveur plus ancien
        // (champ absent) ne doit pas faire dire « aucun logement n'a le
        // service activé » — ce serait une cause inventée.
        service_actif: json?.service_actif !== false,
        // Couche confirmation (2026-08-18) : 'direct' UNIQUEMENT si le serveur
        // l'affirme — absent/inconnu = « l'admin confirme », fail-closed des
        // deux côtés du fil.
        decision_mode: json?.decision_mode === 'direct' ? 'direct' : 'admin_confirms',
        expired: Array.isArray(json?.expired) ? json.expired : [],
      })
    } catch (e) {
      console.error('horaires_list', e)
      setError(e.message || 'Erreur de chargement')
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { refresh() }, [refresh])

  // ⚠️ LES MÉNAGES VIENNENT DE `menage_runs`, LUS PAR LA RLS DE L'ORCHESTRATEUR
  // (`orchestrateur_select_pool`, pool ∧ logIds), exactement comme Suivi équipe.
  // C'est ce qui donne l'exception de durée, l'agent à prévenir et l'heure de
  // début. Aucun endpoint nouveau : la RLS fait foi, ici comme là-bas.
  const visibleEmails = useMemo(() => computeVisibleEmails(currentUser), [currentUser])
  const dates = useMemo(
    () => [...new Set(data.pending.map((b) => b.event_date).filter(Boolean))].sort(),
    [data.pending]
  )
  useEffect(() => {
    // ⚠️ UNE LISTE VIDE N'EST PAS FORCÉMENT « RIEN » (4e forme, CLAUDE.md) :
    // pour l'ADMIN, computeVisibleEmails rend [] parce qu'il n'a PAS de
    // périmètre — couper ici lui aurait silencieusement perdu la durée
    // d'exception, l'agent à prévenir et « ménage commencé ».
    if (!dates.length || (!visibleEmails.length && !voitToutLeParc(currentUser))) { setRuns([]); return }
    let annule = false
    ;(async () => {
      try {
        const { data: rows, error: e } = await buildRunsProviderFilter(
          supabase.from('menage_runs')
            .select('id, logement_id, scheduled_date, status, assigned_provider_email, heure_fixee, duree_min'),
          currentUser, visibleEmails
        ).gte('scheduled_date', dates[0]).lte('scheduled_date', dates[dates.length - 1])
        if (e) throw e
        if (!annule) setRuns(rows || [])
      } catch (e) {
        // Jamais bloquant : sans le run on retombe sur le défaut du logement,
        // et l'écran le dit par le seuil affiché. Mais on ne l'avale pas.
        console.error('[horaires] menage_runs', e?.message || e)
      }
    })()
    return () => { annule = true }
  }, [dates, currentUser, visibleEmails])

  // Les heures ANNONCÉES au formulaire, PAR RUN (arrival + departure brutes,
  // en minutes) — la carte décide seule de ce qui est favorable, contredit ou
  // à ignorer (ADR-226 : information réservée à l'orchestrateur).
  const [annoncesParRun, setAnnoncesParRun] = useState({})
  useEffect(() => {
    const ids = runs.map((r) => r.id).filter(Boolean)
    if (!ids.length) { setAnnoncesParRun({}); return }
    let annule = false
    ;(async () => {
      try {
        const json = await postAction({ action: 'orchestrateur_guest_times', run_id: ids })
        if (annule || !json || typeof json !== 'object') return
        const enMin = (s) => {
          const t2 = normaliserHeure(s)
          if (!t2) return null
          const [h, m2] = t2.split(':').map(Number)
          return h * 60 + m2
        }
        const out = {}
        for (const [rid, v2] of Object.entries(json)) {
          out[rid] = { arrival: enMin(v2?.arrival), departure: enMin(v2?.departure),
            arrivantCount: Number(v2?.arrivant_count) || null }
        }
        setAnnoncesParRun(out)
      } catch (_) { /* silencieux : sans annonce, le contrat fait foi */ }
    })()
    return () => { annule = true }
  }, [runs])

  const runPour = useCallback(
    (logementId, date) => runs.find((r) => r.logement_id === logementId && r.scheduled_date === date) || null,
    [runs]
  )

  // ═══ T4 — CE QUE L'ACCORD CASSE AILLEURS (chevauchement pur, GO Dimitri
  // 2026-08-15). Les AUTRES ménages de l'agent le même jour sont DÉJÀ dans
  // `runs` (RLS pool ∧ logIds — l'admin, lui, voit tout le parc via
  // voitToutLeParc) ; le guichet cloisonné fournit nom et check-out de leurs
  // logements. ⚠️ LIMITE ACTÉE : un ménage hors périmètre de l'orchestrateur
  // reste invisible (roadmap). Pas de trajet : pas de chiffre inventé.
  const [infosAutres, setInfosAutres] = useState({})
  useEffect(() => {
    const ids = [...new Set(runs.map((r) => r.logement_id).filter(Boolean))]
    if (!ids.length) { setInfosAutres({}); return }
    let annule = false
    ;(async () => {
      const entrees = await Promise.all(ids.map(async (lid) => {
        try {
          const info = await fetchLogInfo(lid)
          return [lid, {
            nom: info?.nom || null,
            checkout: info?.timing?.checkout_time || null,
            duree: Number(info?.menage?.menage_duree_min) || 90,
          }]
        } catch (_) { return null } // hors périmètre / erreur → pas de détection
      }))
      if (!annule) setInfosAutres(Object.fromEntries(entrees.filter(Boolean)))
    })()
    return () => { annule = true }
  }, [runs])

  // Les blocs « ailleurs » d'une journée : chaque autre ménage NON terminé de
  // l'agent, avec un début CONNU (heure posée, sinon check-out du logement) —
  // sans début connu, pas de détection : jamais un conflit deviné.
  const autresMenagesPour = useCallback((journee) => {
    const run = runPour(journee.logementId, journee.date)
    const agent = run?.assigned_provider_email
    if (!agent) return []
    return runs
      .filter((r) => r.scheduled_date === journee.date
        && r.assigned_provider_email === agent
        && r.logement_id !== journee.logementId
        && r.status !== 'completed')
      .map((r) => {
        const info = infosAutres[r.logement_id]
        const debutStr = normaliserHeure(r.heure_fixee) || normaliserHeure(info?.checkout)
        if (!debutStr) return null
        const [h, m] = debutStr.split(':').map(Number)
        const debut = h * 60 + m
        const duree = Number(r.duree_min) > 0 ? Number(r.duree_min) : (info?.duree || 90)
        return {
          id: r.id, debut, fin: debut + duree,
          // ⚠️ JAMAIS l'identifiant technique à l'écran (fuite « l2 » attrapée
          // par Dimitri 2026-08-16) — sans nom du guichet, un libellé neutre.
          nom: info?.nom || 'un autre logement',
          prenom: firstNameFromEmail(agent) || agent,
        }
      })
      .filter(Boolean)
  }, [runs, runPour, infosAutres])

  const journees = useMemo(() => {
    // ⚠️ ARRIVÉ SUR UNE DEMANDE PRÉCISE : on ne montre QUE sa journée. Afficher
    // la liste entière obligerait à retrouver, parmi N cartes, celle qu'on vient
    // de toucher — le travail que le clic venait d'épargner.
    const source = cible
      ? data.pending.filter((b) => {
        const vise = data.pending.find((x) => x.id === cible)
        return vise && b.logement_id === vise.logement_id && b.event_date === vise.event_date
      })
      : data.pending
    const list = grouperParJournee(source)
    for (const j of list) {
      const logement = data.logements[j.logementId]
      const contratB = bornesContrat(logement)
      const seuil = seuilMenage(runPour(j.logementId, j.date), logement)
      // ⚠️ LA RÈGLE (2026-08-16) : une demande NON DÉCIDÉE ne contraint pas.
      // « Impossible » se juge par demande, contre les FAITS (contrat + actées
      // + annonces) — jamais contre l'autre pendante.
      const actees = (data.granted_context || []).filter(
        (g) => g.logement_id === j.logementId && g.event_date === j.date)
      const faits = faitsJournee({
        contratBase: contratB, actees, pendantes: j.demandes,
        annoncesRun: annoncesParRun[runPour(j.logementId, j.date)?.id] || null,
      })
      const contrat = faits ? { debut: faits.debut, fin: faits.fin } : null
      j.nbImpossibles = contrat
        ? j.demandes.filter((d) => !bornesCurseur({ demande: d, contrat, seuilMin: seuil }).possible).length
        : j.demandes.length
      j.enConflit = journeeEnConflit({ contrat, demandes: j.demandes, seuilMin: seuil })
    }
    // UNE SEULE LISTE, par ancienneté (version finale 2026-08-16) : le tri par
    // difficulté supposait qu'accorder est une formalité — c'est une décision,
    // et la colonne de couleur dit déjà l'état sans trier.
    const plusVieille = (j) => Math.min(...j.demandes.map((d) => Date.parse(d.created_at) || Infinity))
    return list.sort((a, b) => plusVieille(a) - plusVieille(b))
  }, [data.pending, data.logements, data.granted_context, annoncesParRun, runPour, cible])

  // ACCORDER — le corps ne porte QUE { action, booking_id, granted_time }.
  // JAMAIS un montant ni un tarif : le serveur dérive tout du snapshot.
  const onValider = useCallback(async (journee, demandes, decisions) => {
    // Défense en profondeur : en lecture seule les boutons n'existent pas,
    // mais AUCUNE écriture ne doit pouvoir partir de cet état.
    if (lectureSeule) return
    setBusy(true); setNotice(null)
    try {
      for (const d of demandes) {
        const dec = decisions[d.id]
        if (dec?.refusee) {
          await postAction({ action: 'services_orchestrateur_hourly_refuse', booking_id: d.id })
        } else {
          await postAction({
            action: 'services_orchestrateur_grant',
            booking_id: d.id,
            granted_time: heurePourServeur(dec.heure),
          })
        }
      }
      // C-a (2026-08-21) : la pastille du menu se met à jour TOUT DE SUITE.
      window.dispatchEvent(new Event('fbz:orchestrateur-badges'))
      // ⚠️ LIEN 3 — LA CONFIRMATION REMPLACE LA CARTE, elle ne s'ajoute pas à
      // côté. On dit CE QUI A ÉTÉ DÉCIDÉ (l'heure du voyageur), CE QU'IL RESTE
      // (le temps de ménage) et QUI A ÉTÉ PRÉVENU — puis on propose le seul
      // geste qui suit naturellement : aller voir la journée.
      // ⚠️ INVALIDER LE GUICHET (bug attrapé par le scénario traversant,
      // 2026-08-15) : la plage accordée est servie par `menage_log_info`, que
      // le client met en cache 60 s. Sans invalidation, la frise du Suivi et
      // « Ma journée » montraient L'ANCIENNE fenêtre pendant une minute —
      // silencieux et plausible, la pire espèce. Même règle que l'éditeur de
      // durées, qui documentait déjà ce piège.
      invalidateLogInfo(journee.logementId)
      const contrat = bornesContrat(data.logements[journee.logementId])
      const sim = simuler({ contrat, demandes, decisions })
      setConfirmation({
        jour: journee.date,
        lignes: demandes.map((d) => {
          const dec = decisions[d.id]
          const qui = d.voyageur || 'Le voyageur'
          if (dec?.refusee) return `${qui} garde son horaire de contrat`
          return d.event_kind === EARLY
            ? `${qui} entrera à ${formaterHeureCourte(dec.heure)}`
            : `${qui} partira à ${formaterHeureCourte(dec.heure)}`
        }),
        menage: sim && sim.restantMin > 0
          ? `Le ménage garde ${formaterDuree(sim.restantMin)}, de ${formaterHeureCourte(sim.debut)} à ${formaterHeureCourte(sim.fin)}`
          : null,
        agent: runPour(journee.logementId, journee.date)?.assigned_provider_email || null,
      })
      await refresh()
    } catch (e) {
      console.error('horaires_valider', e)
      setNotice(`Échec : ${e.message}`)
    } finally {
      setBusy(false)
    }
    // ⚠️ `data.logements` ET `runPour` DANS LES DÉPENDANCES. Sans eux, la
    // fermeture capturait l'état du PREMIER rendu — logements vide, donc pas de
    // bornes de contrat, donc pas de simulation : la confirmation s'affichait
    // sans la phrase « Le ménage garde 3 h ». L'écriture partait correctement,
    // seul le compte rendu mentait — c'est-à-dire le pire cas, celui qu'on ne
    // soupçonne pas. Trouvé par la traversée des trois écrans, pas par les
    // tests d'écran isolés, où `onAller` est bouchonné.
  }, [refresh, data.logements, runPour, lectureSeule])

  // REFUSER — le corps ne porte QUE booking_id : l'heure de refus est dérivée
  // SERVEUR. ⚠️ Le libellé ne mentionne AUCUN montant (décision 2026-08-11) —
  // ce que le refus fait de l'empreinte reste une affaire de serveur.
  // La CONFIRMATION du refus est une modale maison (T5 2026-08-15 — le
  // window.confirm natif était le dernier de l'espace, et il ignorait Échap).
  const [refusEnAttente, setRefusEnAttente] = useState(null)

  const onRefuserTout = useCallback((journee, demandes) => {
    if (lectureSeule) return
    setRefusEnAttente({ journee, demandes })
  }, [lectureSeule])

  const confirmerRefus = useCallback(async () => {
    if (lectureSeule || !refusEnAttente) return
    const { demandes } = refusEnAttente
    setRefusEnAttente(null)
    setBusy(true); setNotice(null)
    try {
      for (const d of demandes) {
        await postAction({ action: 'services_orchestrateur_hourly_refuse', booking_id: d.id })
      }
      setNotice('Demande refusée. Le voyageur garde son horaire de contrat.')
      window.dispatchEvent(new Event('fbz:orchestrateur-badges'))
      await refresh()
    } catch (e) {
      console.error('horaires_refuse', e)
      setNotice(`Échec du refus : ${e.message}`)
    } finally {
      setBusy(false)
    }
  }, [refresh, lectureSeule, refusEnAttente])

  // CONFIRMATION ADMIN — passe par api/services (services_admin_validate),
  // LÀ où vivent les gardes de la revue (résa annulée, événement passé,
  // heure ≡ proposée). Les erreurs remontent EN CLAIR.
  const onConfirmerProposition = useCallback(async (d, heureHHMM) => {
    setBusy(true); setNotice(null)
    try {
      await postServices({
        action: 'services_admin_validate',
        booking_id: d.id,
        granted_time: heureHHMM,
      })
      setNotice(d.proposed_kind === 'refuse'
        ? 'Refus confirmé — le voyageur garde son horaire de contrat et est prévenu.'
        : `Confirmé : ${heureCourteDeHHMM(heureHHMM)}. Le voyageur est prévenu.`)
      invalidateLogInfo(d.logement_id)
      await refresh()
    } catch (e) {
      console.error('horaires_confirmer', e)
      const code = e.payload?.error
      setNotice(code === 'proposal_changed'
        ? 'La proposition a changé entre-temps — l’écran vient de se recharger.'
        : code === 'event_passed'
          ? 'L’événement est déjà passé — confirmation refusée (le voyageur est resté à l’heure de contrat).'
          : code === 'reservation_cancelled'
            ? 'La réservation a été annulée — rien à confirmer.'
            : code === 'capture_window_expired'
              ? 'L’empreinte a expiré — la demande partira en « expirée sans décision ».'
              : `Échec : ${e.message}`)
      await refresh()
    } finally {
      setBusy(false)
    }
  }, [refresh])

  const { treated_today: treatedToday, logements, hub_actif: hubActif } = data
  const propositions = (data.pending || []).filter((b) => b.proposed_at)

  return (
    <div style={{ fontFamily: DS.fontBody, color: C.fonce }}>
      <style>{CSS_HORAIRES}</style>

      {/* ════════ CARTE DE TITRE (lot apparence 2026-08-14 — le doublon navy
          shell/page est mort : fond ciel pâle, trait navy qui relie au bandeau) ════════ */}
      <CarteTitrePage
        titre="Demandes d'horaires"
        sousTitre={
          <span data-testid="horaires-sous-titre">
            Accorder ou refuser, en voyant le temps de ménage.
          </span>
        }
        dateTexte={dateDuJourFR()}
        sousLeFilet={(() => {
          // ⚠️ LE BANDEAU NE RÉPÈTE PAS LA LISTE (principe validé sur le Mois) :
          // « N en attente » doublait les cartes juste en dessous. Il dit PAR
          // QUOI COMMENCER : l'impossible d'abord, l'attente ensuite — et la
          // ligne calme quand il n'y a rien.
          const enAttente = data.pending
          if (loading) return null
          if (!enAttente.length) {
            return (
              <div data-testid="horaires-ligne-calme"
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: '50%',
                  flexShrink: 0, background: 'rgba(219,233,226,.9)', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center' }}>
                  <CircleCheck size={16} strokeWidth={2.4} color="#1f5a43" />
                </span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#fff' }}>
                    Aucune demande en attente
                  </span>
                  {/* ⚠️ La fenêtre 6 j / 24 h n'est annoncée que si quelqu'un peut
                      s'en servir : hub éteint OU aucun logement au service actif,
                      on ne promet rien (garanties héritées de l'ancien état vide). */}
                  {data.hub_actif && data.service_actif !== false && (
                    <span style={{ display: 'block', fontSize: 10.5, marginTop: 1, color: 'rgba(255,255,255,.72)' }}>
                      Les voyageurs peuvent demander entre 6 jours et 24 h avant le séjour.
                    </span>
                  )}
                </span>
              </div>
            )
          }
          const impossibles = journees.filter((j) => j.enConflit)
          const nbImpossibles = journees.reduce((s, j) => s + (j.nbImpossibles || 0), 0)
          const agesMin = enAttente
            .map((d) => Date.parse(d.created_at))
            .filter(Number.isFinite)
          const plusVieuxTs = agesMin.length ? Math.min(...agesMin) : null
          const attenteDepuis = (() => {
            if (!plusVieuxTs) return "aujourd'hui"
            const jours = Math.floor((Date.now() - plusVieuxTs) / 86400000)
            const hier = new Date(plusVieuxTs).toDateString() !== new Date().toDateString()
            if (!hier) return "aujourd'hui"
            return jours <= 1 ? 'hier' : `${jours} jours`
          })()
          const agePlusVieille = plusVieuxTs
            ? (() => {
                const min = Math.floor((Date.now() - plusVieuxTs) / 60000)
                if (min < 60) return `${min} min`
                const h = Math.floor(min / 60)
                return h < 24 ? `${h} h` : `${Math.floor(h / 24)} j`
              })()
            : null
          return (
            <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
              {nbImpossibles > 0 && (
                <button type="button" data-testid="horaires-action-impossible"
                  className="fbz-press fbz-cible-tactile"
                  onClick={() => {
                    const el = document.querySelector(`[data-testid="horaires-journee-${impossibles[0].cle}"]`)
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10,
                    background: 'rgba(248,220,209,.94)', border: 'none', borderRadius: 9,
                    padding: '9px 12px', cursor: 'pointer', textAlign: 'left', fontFamily: DS.fontBody }}>
                  <AlertTriangle size={16} strokeWidth={2.2} color="#8f2f1d" style={{ flexShrink: 0 }} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#233240' }}>
                      {nbImpossibles} demande{nbImpossibles > 1 ? 's' : ''} impossible{nbImpossibles > 1 ? 's' : ''}
                    </span>
                    <span style={{ display: 'block', fontSize: 10.5, marginTop: 1, color: '#8f2f1d',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {logements[impossibles[0].logementId]?.nom || impossibles[0].logementId} · aucun créneau ne tient
                    </span>
                  </span>
                </button>
              )}
              <div data-testid="horaires-action-attente"
                style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10,
                  background: 'rgba(255,255,255,.14)', borderRadius: 9,
                  padding: '9px 12px', fontFamily: DS.fontBody }}>
                <Clock size={16} strokeWidth={2.2} color="#fff" style={{ flexShrink: 0 }} />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#fff' }}>
                    {enAttente.length} attend{enAttente.length > 1 ? 'ent' : ''} depuis {attenteDepuis}
                  </span>
                  {agePlusVieille && (
                    <span style={{ display: 'block', fontSize: 10.5, marginTop: 1, color: 'rgba(255,255,255,.72)' }}>
                      la plus ancienne : {agePlusVieille}
                    </span>
                  )}
                </span>
              </div>
            </div>
          )
        })()}
        actions={<>
          {/* ⚠️ LA FLÈCHE N'EXISTE QUE SI L'ON EST ARRIVÉ D'AILLEURS : affichée
              en permanence, elle promettrait un retour vers un écran jamais
              quitté. */}
          {retour && (
            <button type="button" data-testid="horaires-retour" onClick={() => onAller && onAller(retour)}
              aria-label={libelleRetour({ retour })} title={libelleRetour({ retour })}
              className="fbz-press"
              style={{ ...boutonCarteTitre(), width: 40, height: 40, padding: 0,
                justifyContent: 'center' }}>
              <ArrowLeft size={17} strokeWidth={2.2} />
            </button>
          )}
          {/* ⚠️ « MODIFIER » — ADMIN SEULEMENT, même triple signal que la frise
              du Suivi (bouton plein CORAIL actif — le corail ne sert qu'aux
              alertes, et un mode d'argent OUVERT en est une — + libellé
              « Terminer » + curseurs figés sinon). */}
          {estAdmin && (
            <button type="button" data-testid="horaires-bouton-modifier"
              onClick={() => setModeModif((v) => !v)}
              className="fbz-press"
              style={boutonCarteTitre({ actif: modeModif })}>
              {modeModif ? 'Modification active · Terminer' : 'Modifier'}
            </button>
          )}
        </>} />
      {basculePage}

      {/* 1100 comme partout (T1 2026-08-15) — le 760 local divergeait du gabarit. */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '18px 0 40px' }}>
        {confirmation && (
          <div data-testid="horaires-confirmation"
            style={{ background: C.vertFond, borderRadius: 12, padding: '14px 16px',
              marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <CircleCheck size={16} strokeWidth={2.2} color={C.vertLabel}
                style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ minWidth: 0 }}>
                {confirmation.lignes.map((l, i) => (
                  <div key={i} style={{ fontSize: 13, fontWeight: 700, color: C.vertTexte }}>{l}</div>
                ))}
                {confirmation.menage && (
                  <div style={{ fontSize: 11.5, color: C.vertTexte, marginTop: 3 }}>
                    {confirmation.menage}
                  </div>
                )}
              </div>
            </div>
            {confirmation.agent && (
              <div data-testid="horaires-confirmation-agent"
                style={{ marginTop: 10, padding: '8px 12px', background: C.notifFond,
                  borderRadius: 9, display: 'flex', gap: 7, alignItems: 'center' }}>
                <Bell size={13} strokeWidth={2.2} color={C.notifIcone} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 10.5, color: C.notifTexte }}>
                  Le créneau de {firstNameFromEmail(confirmation.agent) || confirmation.agent} est mis à jour.
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button type="button" data-testid="horaires-voir-journee"
                // ⚠️ SI L'ON VIENT DE LA FRISE, on y RETOURNE — avec le retour
                // qu'elle portait. Fabriquer une destination neuve perdrait le
                // chemin vers le mois, et enfermerait dans la frise.
                onClick={() => onAller && onAller(
                  retour && retour.onglet === ONGLET_FRISE
                    ? retour
                    : versLaFrise(confirmation.jour, retour)
                )}
                style={boutonPlein(C.gris, false)}>
                <CalendarRange size={14} strokeWidth={2} style={{ marginRight: 6 }} />
                Voir la journée du {new Date(`${confirmation.jour}T12:00:00`).getDate()}
              </button>
              <button type="button" data-testid="horaires-fermer-confirmation"
                onClick={() => { setConfirmation(null); setCible(null) }}
                style={boutonSecondaire(false)}>Fermer</button>
            </div>
          </div>
        )}

        {notice && (
          <div data-testid="horaires-notice"
            style={{ background: C.cielPale, color: C.gris, borderRadius: 9,
              padding: '10px 14px', fontSize: 12.5, marginBottom: 14 }}>
            {notice}
          </div>
        )}

        {loading && <div style={{ color: C.muet, fontSize: 13 }}>Chargement…</div>}

        {!loading && error && (
          <div style={{ background: DS.dangerBg, color: DS.dangerText, borderRadius: 9,
            padding: '10px 14px', fontSize: 13 }}>{error}</div>
        )}

        {/* ⚠️ L'ÉTAT VIDE DIT LA VRAIE CAUSE (demande Dimitri 2026-08-14).
            « Aucune demande » quand le service tourne est une bonne nouvelle ;
            quand aucun logement n'a le service activé, c'est un état de
            CONFIGURATION — le même écran vide pour les deux ferait chercher des
            demandes qui ne peuvent pas exister. */}
        {/* ⚠️ L'état vide « aucune demande » vit désormais dans la LIGNE CALME du
            bandeau (le corps ne le répète pas) — le corps ne garde que l'état de
            CONFIGURATION de #1216, qui est une autre information. */}
        {!loading && !error && journees.length === 0 && hubActif && data.service_actif === false && (
          <div data-testid="horaires-vide"
            style={{ background: C.colonne, borderRadius: 12, padding: '26px 20px',
              textAlign: 'center', color: C.texteDoux, fontSize: 13.5, lineHeight: 1.6 }}>
            <span data-testid="horaires-vide-sans-service">
              Aucun logement n'a le service activé — aucune demande ne peut
              arriver ici.
            </span>
          </div>
        )}

        {/* ════════ À CONFIRMER (admin) — couche proposition/confirmation ════════ */}
        {!loading && !error && estAdmin && propositions.length > 0 && (
          <SectionAConfirmer
            propositions={propositions}
            logements={logements}
            busy={busy}
            onConfirmer={onConfirmerProposition} />
        )}

        {!loading && !error && journees.map((j) => (
          <JourneeCartes
            key={j.cle}
            journee={j}
            logement={logements[j.logementId]}
            run={runPour(j.logementId, j.date)}
            annoncesRun={annoncesParRun[runPour(j.logementId, j.date)?.id] || null}
            grantedContext={data.granted_context}
            nowMin={nowMin}
            estAdmin={estAdmin}
            onNavigate={onNavigate}
            onValider={onValider}
            onRefuserTout={onRefuserTout}
            busy={busy}
            lectureSeule={lectureSeule}
            autresMenages={autresMenagesPour(j)} />
        ))}

        {/* ════════ TRAITÉES AUJOURD'HUI ════════ */}
        {refusEnAttente && (
          <ModaleRefus
            demandes={refusEnAttente.demandes}
            busy={busy}
            onFermer={() => setRefusEnAttente(null)}
            onConfirmer={confirmerRefus} />
        )}

        {!loading && !error && treatedToday.length > 0 && (
          <section style={{ marginTop: 26 }}>
            <h2 style={{ fontFamily: DS.fontBody, fontSize: 10.5, fontWeight: 700,
              color: C.muet, textTransform: 'uppercase', letterSpacing: '.09em',
              margin: '0 0 8px' }}>
              Traitées aujourd'hui
            </h2>
            {treatedToday.map((b) => (
              <LigneTraitee key={b.id} booking={b}
                nomLogement={logements[b.logement_id]?.nom || b.logement_id}
                traitementLabel={formatHeureDecision(b.validated_at)} />
            ))}
          </section>
        )}

        {/* ════════ HISTORIQUE PAR MOIS (GO Dimitri 2026-08-17) ════════ */}
        {!loading && !error && hubActif && <SectionHistorique />}
      </div>
    </div>
  )
}

// ── À CONFIRMER (admin) — les propositions de l'orchestrateur (couche
// confirmation, GO Dimitri 2026-08-18). Chaque ligne : la demande, l'heure
// proposée, l'ÂGE de la proposition, et l'URGENCE calée sur la vraie
// horloge : min(fin de fenêtre de capture = création + 6 j, l'événement
// lui-même) — jamais les 7 jours Stripe (finding H2). Urgent (< 48 h) →
// terracotta « ! ».
function SectionAConfirmer({ propositions, logements, busy, onConfirmer }) {
  const maintenant = Date.now()
  const echeanceMs = (b) => {
    const capture = new Date(b.created_at).getTime() + 6 * 24 * 3600 * 1000
    const evt = b.event_date ? new Date(`${b.event_date}T12:00:00`).getTime() : Infinity
    return Math.min(capture, Number.isNaN(evt) ? Infinity : evt)
  }
  const urgentes = propositions.filter((b) => echeanceMs(b) - maintenant < 48 * 3600 * 1000)
  const plusAncienneH = Math.floor(
    (maintenant - Math.min(...propositions.map((b) => new Date(b.proposed_at).getTime()))) / 3600000,
  )
  return (
    <section data-testid="horaires-a-confirmer" style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <h2 style={{ fontFamily: DS.fontBody, fontSize: 10.5, fontWeight: 700,
          color: C.gris, textTransform: 'uppercase', letterSpacing: '.09em', margin: 0 }}>
          À confirmer
        </h2>
        <span data-testid="horaires-a-confirmer-compte"
          style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 9px',
            background: urgentes.length ? C.fondAlerte : C.cielPale,
            color: urgentes.length ? C.texteAlerte : C.gris }}>
          {propositions.length}{urgentes.length ? ' !' : ''}
        </span>
        {plusAncienneH >= 1 && (
          <span style={{ fontSize: 11, color: C.texteDoux }}>
            — la plus ancienne attend depuis {plusAncienneH} h
          </span>
        )}
      </div>
      {propositions.map((b) => {
        const refus = b.proposed_kind === 'refuse'
        const urgente = echeanceMs(b) - maintenant < 48 * 3600 * 1000
        const verbe = b.event_kind === 'late_checkout' ? 'partirait' : 'arriverait'
        const nomV = b.voyageur || 'Le voyageur'
        return (
          <div key={b.id} data-testid={`horaires-a-confirmer-${b.id}`}
            style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '10px 13px', background: '#fff', borderRadius: 9, marginBottom: 6,
              border: `1px solid ${urgente ? C.bordAlerte : C.bord}` }}>
            <span style={{ fontSize: 12, color: C.fonce, fontFamily: DS.fontBody }}>
              <strong>{logements[b.logement_id]?.nom || 'Logement'}</strong>
              {' · '}
              {refus
                ? `refus proposé pour ${nomV} — ${heureCourteDeHHMM(b.requested_time)} demandé`
                : `${nomV} ${verbe} à ${heureCourteDeHHMM(b.proposed_time)} — ${heureCourteDeHHMM(b.requested_time)} demandé`}
            </span>
            {urgente && (
              <span data-testid={`horaires-a-confirmer-urgence-${b.id}`}
                style={{ fontSize: 10.5, fontWeight: 700, color: C.texteAlerte }}>
                l'empreinte expire bientôt
              </span>
            )}
            <button type="button" data-testid={`horaires-confirmer-${b.id}`}
              disabled={busy}
              onClick={() => onConfirmer(b, normaliserHeure(refus ? b.standard_time : b.proposed_time))}
              className="fbz-press fbz-cible-tactile"
              style={{ ...boutonPlein(C.gris, busy), marginLeft: 'auto' }}>
              {refus ? 'Confirmer le refus' : `Confirmer ${heureCourteDeHHMM(b.proposed_time)}`}
            </button>
          </div>
        )
      })}
    </section>
  )
}

// ── LA LIGNE-PHRASE d'une demande traitée — PARTAGÉE entre « Traitées
// aujourd'hui » et l'historique par mois : le même langage garanti par
// CONSTRUCTION, pas par discipline (GO Dimitri 2026-08-17). La ligne se limite
// à : logement, date du séjour, phrase, (montant côté admin en historique),
// date/heure de traitement en gris à droite. DES PHRASES SIMPLES, jamais un
// empilement de fragments — et jamais de pronom accordé sur un prénom réel
// (« — 15h demandé » plutôt que « il demandait 15h »).
function LigneTraitee({ booking: b, nomLogement, montantCents, traitementLabel, testidPrefix = 'horaires' }) {
  const refusee = b.status === 'cancelled'
  const verbe = b.event_kind === 'late_checkout' ? 'partira' : 'arrivera'
  const nomV = b.voyageur || 'Le voyageur'
  const phrase = refusee
    ? `${nomV} reste sur son heure de contrat, ${heureCourteDeHHMM(b.standard_time)}.`
    : b.granted_time === b.requested_time
      ? `${nomV} ${verbe} à ${heureCourteDeHHMM(b.granted_time)}, comme demandé.`
      : `${nomV} ${verbe} à ${heureCourteDeHHMM(b.granted_time)} — ${heureCourteDeHHMM(b.requested_time)} demandé.`
  const dateSejour = b.event_date ? formatDateFr(b.event_date) : null
  return (
    <div data-testid={`${testidPrefix}-traitee-${b.id}`}
      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '9px 12px', background: '#fff', border: `1px solid ${C.bord}`,
        borderRadius: 9, marginBottom: 6, fontSize: 11.5 }}>
      <span style={{ fontWeight: 700, color: C.gris }}>
        {nomLogement}
        {dateSejour ? ` · ${dateSejour}` : ''}
      </span>
      <span data-testid={`${testidPrefix}-decision-${b.id}`}
        style={{ color: refusee ? C.texteAlerte : C.vertTexte }}>
        {phrase}
      </span>
      {/* MONTANT — uniquement quand le serveur l'a servi (lecteur admin,
          demande accordée). L'orchestrateur ne le reçoit jamais. */}
      {typeof montantCents === 'number' && !refusee && (
        <span data-testid={`${testidPrefix}-montant-${b.id}`}
          style={{ marginLeft: 'auto', fontWeight: 700, color: C.gris, fontSize: 11 }}>
          {formaterEuros(montantCents)}
        </span>
      )}
      {/* La date/heure de traitement : l'info la moins utile — à droite,
          discrète. */}
      <span style={{ color: C.muet, fontSize: 10, opacity: .75,
        ...(typeof montantCents === 'number' && !refusee ? {} : { marginLeft: 'auto' }) }}>
        {traitementLabel}
      </span>
    </div>
  )
}

/** 1949 → « 19,49 € » — jamais un montant en centimes à l'écran. */
function formaterEuros(cents) {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`
}

/** « 2026-08 » → « août 2026 » ; mois précédent/suivant en calendrier civil. */
function libelleMois(mois) {
  try {
    return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' })
      .format(new Date(`${mois}-01T12:00:00`))
  } catch (_) { return mois }
}
function moisVoisin(mois, delta) {
  const [a, m] = mois.split('-').map(Number)
  const total = a * 12 + (m - 1) + delta
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`
}

// ── L'HISTORIQUE COMPLET, PAR MOIS — section REPLIÉE en bas de l'écran
// (GO Dimitri 2026-08-17). Elle ne charge RIEN avant d'être ouverte ; un mois
// = une requête (mise en cache pour la session de l'écran). Le mois d'une
// demande = son mois de TRAITEMENT, cohérent avec « Traitées aujourd'hui ».
// Les montants n'apparaissent que si le serveur les a servis (lecteur admin).
function SectionHistorique() {
  const moisCourant = todayParisIso().slice(0, 7)
  const [ouvert, setOuvert] = useState(false)
  const [mois, setMois] = useState(moisCourant)
  const [chargement, setChargement] = useState(false)
  const [erreur, setErreur] = useState(null)
  const [parMois, setParMois] = useState({}) // { 'YYYY-MM': réponse serveur }

  const donnees = parMois[mois] || null
  // La borne basse vient de N'IMPORTE QUELLE réponse déjà reçue (le serveur la
  // répète) — sans réponse, on ne sait pas et on ne borne que le futur.
  const premierMois = Object.values(parMois).map((d) => d.premier_mois).find(Boolean) || null

  useEffect(() => {
    if (!ouvert || parMois[mois]) return
    let annule = false
    setChargement(true)
    setErreur(null)
    postAction({ action: 'services_orchestrateur_hourly_history', month: mois })
      .then((json) => { if (!annule) setParMois((prev) => ({ ...prev, [mois]: json })) })
      .catch((e) => {
        console.error('[horaires] historique', e)
        if (!annule) setErreur("L'historique n'a pas pu être chargé.")
      })
      .finally(() => { if (!annule) setChargement(false) })
    return () => { annule = true }
  }, [ouvert, mois, parMois])

  const bookings = donnees?.bookings || []
  const accordees = bookings.filter((b) => b.status === 'confirmed').length
  const refusees = bookings.filter((b) => b.status === 'cancelled').length
  const arrivees = bookings.filter((b) => b.event_kind === 'early_checkin').length
  const departs = bookings.filter((b) => b.event_kind === 'late_checkout').length
  const s = (n) => (n > 1 ? 's' : '')

  return (
    <section style={{ marginTop: 26 }}>
      <button type="button" data-testid="horaires-historique-toggle"
        onClick={() => setOuvert((o) => !o)} className="fbz-press fbz-cible-tactile"
        aria-expanded={ouvert}
        style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'none',
          border: 'none', padding: '6px 0', cursor: 'pointer', fontFamily: DS.fontBody,
          fontSize: 10.5, fontWeight: 700, color: C.muet, textTransform: 'uppercase',
          letterSpacing: '.09em' }}>
        <span aria-hidden="true" style={{ display: 'inline-block', fontSize: 9,
          transform: ouvert ? 'rotate(90deg)' : 'none', transition: 'transform .12s ease' }}>▶</span>
        Historique
      </button>

      {ouvert && (
        <div data-testid="horaires-historique">
          {/* Le sélecteur de mois — borné [première demande, mois courant]. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 10px' }}>
            <button type="button" data-testid="horaires-historique-mois-prec"
              onClick={() => setMois((m) => moisVoisin(m, -1))}
              disabled={chargement || (premierMois != null && mois <= premierMois)}
              className="fbz-press fbz-cible-tactile" aria-label="Mois précédent"
              style={boutonSecondaire(chargement || (premierMois != null && mois <= premierMois))}>‹</button>
            <span data-testid="horaires-historique-mois" style={{ fontFamily: DS.fontDisplay,
              fontSize: 14.5, fontWeight: 600, color: C.fonce, minWidth: 130, textAlign: 'center' }}>
              {libelleMois(mois)}
            </span>
            <button type="button" data-testid="horaires-historique-mois-suiv"
              onClick={() => setMois((m) => moisVoisin(m, 1))}
              disabled={chargement || mois >= moisCourant}
              className="fbz-press fbz-cible-tactile" aria-label="Mois suivant"
              style={boutonSecondaire(chargement || mois >= moisCourant)}>›</button>
          </div>

          {chargement && <div style={{ color: C.muet, fontSize: 13 }}>Chargement…</div>}
          {!chargement && erreur && (
            <div style={{ background: DS.dangerBg, color: DS.dangerText, borderRadius: 9,
              padding: '10px 14px', fontSize: 13 }}>{erreur}</div>
          )}

          {!chargement && !erreur && donnees && bookings.length === 0 && (
            <div style={{ color: C.texteDoux, fontSize: 13, padding: '4px 0 8px' }}>
              Aucune demande traitée en {libelleMois(mois)}.
            </div>
          )}

          {!chargement && !erreur && bookings.length > 0 && (
            <>
              {/* LE COMPTE EN TÊTE — ventilé arrivées anticipées / départs
                  tardifs : ce ne sont pas les mêmes demandes. */}
              <div data-testid="horaires-historique-compte"
                style={{ fontSize: 12.5, color: C.gris, marginBottom: 10, lineHeight: 1.55 }}>
                <strong>{bookings.length} demande{s(bookings.length)}</strong>
                {' · '}{accordees} accordée{s(accordees)} · {refusees} refusée{s(refusees)}
                <span style={{ color: C.texteDoux }}>
                  {' — '}dont {arrivees} arrivée{s(arrivees)} anticipée{s(arrivees)} et{' '}
                  {departs} départ{s(departs)} tardif{s(departs)}
                </span>
              </div>
              {bookings.map((b) => (
                <LigneTraitee key={b.id} booking={b} testidPrefix="horaires-histo"
                  nomLogement={donnees.logements?.[b.logement_id]?.nom || 'Logement'}
                  montantCents={typeof b.amount_eur_cents === 'number' ? b.amount_eur_cents : undefined}
                  traitementLabel={`${new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' })
                    .format(new Date(b.validated_at))} · ${formatHeureDecision(b.validated_at)}`} />
              ))}
            </>
          )}
        </div>
      )}
    </section>
  )
}

// La modale de confirmation du refus — maison, Échap compris (hook partagé).
// ⚠️ « sera prévenu par email » est VRAI : l'email de décision est réel, non
// gaté par MESSAGES_DRY_RUN (vérifié 2026-08-15 — doctrine transactionnelle).
function ModaleRefus({ demandes, busy, onFermer, onConfirmer }) {
  useEchapPourFermer(onFermer)
  const plusieurs = demandes.length > 1
  return (
    <div role="dialog" aria-modal="true" aria-label="Refuser la demande"
      data-testid="horaires-modale-refus"
      style={{ position: 'fixed', inset: 0, background: 'rgba(46,61,77,.42)', zIndex: 80,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, maxWidth: 420, width: '100%',
        padding: '18px 20px', fontFamily: DS.fontBody }}>
        <div style={{ fontFamily: DS.fontDisplay, fontSize: 16, fontWeight: 600,
          color: C.fonce, marginBottom: 8 }}>
          Refuser {plusieurs ? 'ces demandes' : 'cette demande'} ?
        </div>
        <div style={{ fontSize: 12.5, color: C.texteDoux, lineHeight: 1.6, marginBottom: 16 }}>
          Le voyageur gardera son horaire de contrat et sera prévenu par email.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" data-testid="horaires-refus-annuler"
            onClick={onFermer} className="fbz-press fbz-cible-tactile"
            style={boutonSecondaire(false)}>
            Annuler
          </button>
          <button type="button" data-testid="horaires-refus-confirmer"
            disabled={busy} onClick={onConfirmer} className="fbz-press fbz-cible-tactile"
            style={boutonPlein(C.gris, busy)}>
            {plusieurs ? 'Tout refuser' : 'Refuser'}
          </button>
        </div>
      </div>
    </div>
  )
}


export { todayParisIso }
